/* eslint-disable */
// ============================================================
//  SCREENS MAIN - Dashboard et tabs principaux
// ============================================================

import { useState, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { NIGHT_BG, NightSparkles, C, THEMES, applyTheme, getCurrentTheme, PrimaryButton } from "./theme";
import { supabase, getBlockedIds, getMyPros, getPros, trackGuestEvent } from "./supabase";
import { setBadge, pushSupported, enablePush } from "./push";
import { TabPros } from "./screens/pros";
import { PRO_METIERS } from "./constants";
import {
  T,
  LOGO,
  ADS,
  MARKET_ITEMS,
  CATEGORIES,
  CAT_COLOR,
  COND_COL,
  MAP_HTML,
  MSUBCATS,
  COUNTRIES,
  PRO_FAMILIES,
  PUBLISH_CATEGORIES,
} from "./constants";
import {
  useLang,
  getProximityScore,
  getAdCoords,
  geocodeCity,
  getDisplayName,
  getKnownPosition,
  compareByDistance,
} from "./utils";
import { useBackHandler } from "./back";
import {
  G,
  CIcon,
  AdCard,
  SearchAdvancedModal,
  NotificationBell,
  Header,
  SignupPrompt,
} from "./ui";
import {
  SplashScreen,
  LangScreen,
  LegalScreen,
  AuthChoice,
  LoginScreen,
  RegisterScreen,
  ForgotPasswordScreen,
  ContactSAVScreen,
} from "./screens-auth";
// Imports de screens-detail et screens-profile supprimés : étaient orphelins
// (aucune utilisation dans ce fichier) et bloquaient le code-splitting du
// bundle initial (webpack incluait ces modules de 3400 et 2100 lignes dans
// main.js). Le routing vers ces écrans se fait via navigate() dans App.jsx.

export function TabHome({onAd,onPublish,search,setSearch,onAgenda,onAdvanced,onNotifications,lang="fr",ads=[],filters={},user={},navigate=()=>{}}) {
  const t = useLang(lang);
  const [cat,setCat]=useState(null);
  const [showAdvanced,setShowAdvanced]=useState(false);
  // Pas de repli sur "CH" : un pays inconnu doit donner un classement NEUTRE,
  // pas un classement suisse. Avec l'ancien repli, un membre belge voyait les
  // annonces suisses remonter avant les siennes et le bandeau affichait 🇨🇭.
  const userCountry = user?.country || "";
  // Position connue (si geoloc deja accordee) -> tri "le plus proche d'abord"
  const [userPos,setUserPos]=useState(null);
  useEffect(()=>{ let on=true; getKnownPosition().then(p=>{ if(on&&p) setUserPos(p); }); return ()=>{on=false}; },[]);

  // Encart d'incitation à activer les notifications (une fois, utilisateur connecté,
  // permission pas encore décidée). i18n inline pour rester contenu.
  const PN_I18N = {
    fr:{title:"Active les notifications",desc:"Sois alerté des messages et de l'activité de tes annonces.",activate:"Activer",later:"Plus tard"},
    en:{title:"Turn on notifications",desc:"Get alerted about messages and your listings' activity.",activate:"Enable",later:"Later"},
    de:{title:"Benachrichtigungen aktivieren",desc:"Werde über Nachrichten und Anzeigen-Aktivität informiert.",activate:"Aktivieren",later:"Später"},
    es:{title:"Activa las notificaciones",desc:"Recibe avisos de mensajes y la actividad de tus anuncios.",activate:"Activar",later:"Más tarde"},
    it:{title:"Attiva le notifiche",desc:"Ricevi avvisi su messaggi e attività dei tuoi annunci.",activate:"Attiva",later:"Più tardi"},
    pt:{title:"Ativa as notificações",desc:"Recebe alertas de mensagens e atividade dos teus anúncios.",activate:"Ativar",later:"Mais tarde"},
    nl:{title:"Meldingen inschakelen",desc:"Krijg meldingen over berichten en activiteit op je advertenties.",activate:"Inschakelen",later:"Later"},
  };
  const PN = PN_I18N[lang] || PN_I18N.fr;
  const [showPushNudge,setShowPushNudge]=useState(false);
  useEffect(() => {
    try {
      if (user?.id && pushSupported() && Notification.permission === "default" && localStorage.getItem("hc_push_nudge_dismissed") !== "true") {
        setShowPushNudge(true);
      }
    } catch(e) {}
  }, [user]);
  const activatePush = async () => { try { await enablePush(); } catch(e) {} setShowPushNudge(false); };
  const dismissPush = () => { try { localStorage.setItem("hc_push_nudge_dismissed","true"); } catch(e) {} setShowPushNudge(false); };
  const userRegion = user?.region || "";

  // Recherche IA : barre de recherche assistee (langage naturel). i18n inline.
  const AIS_I18N = {
    fr:{hint:"Aide ta recherche avec l'IA",loading:"L'IA cherche…",results:"Résultats IA",clear:"Effacer",error:"Recherche IA indisponible, réessaie.",needQuery:"Écris d'abord ta recherche ci-dessus.",login:"Connecte-toi pour utiliser la recherche IA.",none:"Aucune annonce ne correspond à ta demande."},
    en:{hint:"Get AI help with your search",loading:"AI is searching…",results:"AI results",clear:"Clear",error:"AI search unavailable, try again.",needQuery:"Type your search above first.",login:"Log in to use AI search.",none:"No listing matches your request."},
    de:{hint:"Suche mit KI-Hilfe",loading:"KI sucht…",results:"KI-Ergebnisse",clear:"Löschen",error:"KI-Suche nicht verfügbar, versuche es erneut.",needQuery:"Gib oben zuerst deine Suche ein.",login:"Melde dich an, um die KI-Suche zu nutzen.",none:"Keine Anzeige passt zu deiner Anfrage."},
    it:{hint:"Fatti aiutare dall'IA nella ricerca",loading:"L'IA sta cercando…",results:"Risultati IA",clear:"Cancella",error:"Ricerca IA non disponibile, riprova.",needQuery:"Scrivi prima la tua ricerca qui sopra.",login:"Accedi per usare la ricerca IA.",none:"Nessun annuncio corrisponde alla tua richiesta."},
    es:{hint:"Deja que la IA te ayude a buscar",loading:"La IA está buscando…",results:"Resultados IA",clear:"Borrar",error:"Búsqueda IA no disponible, inténtalo de nuevo.",needQuery:"Escribe primero tu búsqueda arriba.",login:"Inicia sesión para usar la búsqueda IA.",none:"Ningún anuncio coincide con tu búsqueda."},
    pt:{hint:"Deixa a IA ajudar na tua pesquisa",loading:"A IA está a procurar…",results:"Resultados IA",clear:"Limpar",error:"Pesquisa IA indisponível, tenta de novo.",needQuery:"Escreve primeiro a tua pesquisa acima.",login:"Inicia sessão para usar a pesquisa IA.",none:"Nenhum anúncio corresponde ao teu pedido."},
    nl:{hint:"Laat de AI je zoekopdracht helpen",loading:"De AI zoekt…",results:"AI-resultaten",clear:"Wissen",error:"AI-zoekfunctie niet beschikbaar, probeer opnieuw.",needQuery:"Typ eerst je zoekopdracht hierboven.",login:"Meld je aan om de AI-zoekfunctie te gebruiken.",none:"Geen enkele advertentie komt overeen met je vraag."},
  };
  const aiT = AIS_I18N[lang] || AIS_I18N.fr;
  const [aiLoading,setAiLoading]=useState(false);
  const [aiIds,setAiIds]=useState(null); // null = IA inactive ; array = resultats
  const [aiMsg,setAiMsg]=useState("");
  // Fiches Pro pour rendre la recherche Accueil universelle (annonces + shop + pros).
  const [pros,setPros]=useState([]);
  const [aiProIds,setAiProIds]=useState(null);
  useEffect(()=>{ let on=true; getPros().then(d=>{ if(on) setPros(d||[]); }); return ()=>{on=false}; },[]);
  const runAiSearch = async () => {
    const q = (search||"").trim();
    setAiMsg("");
    if (!q) { setAiMsg(aiT.needQuery); return; }
    if (!user?.id) { setAiMsg(aiT.login); return; }
    setAiLoading(true);
    try {
      // Pool = TOUTES les annonces (annonces + shop) + fiches Pro -> "tout est trouvable".
      const adPool = (ads||[]).slice(0,120).map(a => ({ id:a.id, cat:a.cat, title:a.title, price:a.price, region:a.region, city:a.city, loc:a.loc, breed:a.horseBreed, sex:a.horseSex, level:a.horseLevel, discipline:a.horseDiscipline, age:a.horseAge, desc:a.desc }));
      const proPool = (pros||[]).slice(0,80).map(p => ({ id:"pro_"+p.id, cat:p.family, title:p.name, city:p.city, region:p.region, loc:[p.city,p.region].filter(Boolean).join(", "), desc:p.description }));
      const { data, error } = await supabase.functions.invoke("ai-search", { body: { query:q, ads:[...adPool,...proPool], lang } });
      if (error || !data || data.error || !Array.isArray(data.ids)) { setAiMsg(aiT.error); }
      else {
        const proIds = data.ids.filter(id => String(id).startsWith("pro_")).map(id => String(id).slice(4));
        const adIds = data.ids.filter(id => !String(id).startsWith("pro_"));
        setAiIds(adIds); setAiProIds(proIds);
        if (data.ids.length===0) setAiMsg(aiT.none);
      }
    } catch(e) { setAiMsg(aiT.error); }
    setAiLoading(false);
  };
  const clearAiSearch = () => { setAiIds(null); setAiProIds(null); setAiMsg(""); };

  let filtered = [];
  try {
    // Exclure les annonces du marketplace (gear) car elles ont leur propre tab
    const adsNoGear = ads.filter(a => a.cat !== "gear" && a.category !== "gear");
    filtered=adsNoGear.filter(a=>{
    // Filtre catégorie
    const matchC=!cat||a.cat===cat;
    
    // Recherche texte intelligente : titre + description + tags + localisation + vendeur
    const q=search.toLowerCase().trim();
    const matchS=!q||
      a.title?.toLowerCase().includes(q)||
      a.desc?.toLowerCase().includes(q)||
      a.loc?.toLowerCase().includes(q)||
      a.seller?.toLowerCase().includes(q)||
      a.region?.toLowerCase().includes(q)||
      a.city?.toLowerCase().includes(q)||
      (a.tags||[]).some(tg=>tg.toLowerCase().includes(q))||
      (a.coverageRegions||[]).some(r=>r.toLowerCase().includes(q));
    
    // Filtres avancés
    const f = filters||{};
    
    // Pays
    const matchCountry = !f.country || a.country === f.country;
    
    // Région (soit la région principale, soit dans la coverage)
    const matchRegion = !f.region || a.region === f.region || (a.coverageRegions||[]).includes(f.region);
    
    // Prix
    const priceNum = parseFloat((a.price||"").replace(/[^0-9.]/g,""))||0;
    const matchPriceMin = !f.priceMin || priceNum >= parseFloat(f.priceMin);
    const matchPriceMax = !f.priceMax || priceNum <= parseFloat(f.priceMax);
    
    // Vérifié uniquement
    const matchVerif = !f.verifOnly || a.verified;
    
    // Filtres chevaux (s'appliquent seulement si l'annonce est en catégorie sale et a les infos)
    const matchBreed = !f.horseBreed || a.horseBreed === f.horseBreed;
    const matchSex = !f.horseSex || a.horseSex === f.horseSex;
    const matchLevel = !f.horseLevel || a.horseLevel === f.horseLevel;
    const matchDiscipline = !f.horseDiscipline || (a.horseDiscipline||[]).includes(f.horseDiscipline);
    
    // Filtre âge (par tranche)
    let matchAge = true;
    if (f.horseAgeRange && a.horseAge != null) {
      const ranges = { poulain:[0,2], jeune:[3,7], adulte:[8,15], veteran:[16,99] };
      const r = ranges[f.horseAgeRange];
      if (r) matchAge = a.horseAge >= r[0] && a.horseAge <= r[1];
      else matchAge = false;
    } else if (f.horseAgeRange && a.horseAge == null) {
      matchAge = false;
    }
    
    return matchC&&matchS&&matchCountry&&matchRegion&&matchPriceMin&&matchPriceMax&&matchVerif&&matchBreed&&matchSex&&matchLevel&&matchDiscipline&&matchAge;
  });
  } catch(err) {
    console.error("Filter error:", err);
    filtered = ads || [];
  }
  
  // Appliquer le tri (avec score de proximité par défaut)
  let sorted = [];
  try {
    sorted = [...filtered].sort((a,b)=>{
    const f = filters||{};
    if (f.sortBy === "price_asc") {
      return (parseFloat((a.price||"").replace(/[^0-9.]/g,""))||0) - (parseFloat((b.price||"").replace(/[^0-9.]/g,""))||0);
    } else if (f.sortBy === "price_desc") {
      return (parseFloat((b.price||"").replace(/[^0-9.]/g,""))||0) - (parseFloat((a.price||"").replace(/[^0-9.]/g,""))||0);
    } else if (f.sortBy === "popular") {
      return (b.views||0) - (a.views||0);
    }
    // Default : distance reelle si la position est connue (geoloc deja accordee),
    // sinon tri par proximité grossiere (région > pays > voisins > monde)
    if (userPos) {
      return compareByDistance(a, b, userPos, (x, y) =>
        getProximityScore(y, userCountry, userRegion) - getProximityScore(x, userCountry, userRegion));
    }
    const scoreA = getProximityScore(a, userCountry, userRegion);
    const scoreB = getProximityScore(b, userCountry, userRegion);
    if (scoreA !== scoreB) return scoreB - scoreA;
    // En cas d'égalité, plus récent en premier
    return 0;
  });
  } catch(err) {
    console.error("Sort error:", err);
    sorted = filtered;
  }

  // Recherche IA active : on affiche les annonces retournees par l'IA (dans son ordre
  // de pertinence), sinon la liste normale triee.
  const aiActive = aiIds !== null;
  const displayed = aiActive
    ? aiIds.map(id => (ads||[]).find(a => a.id === id)).filter(Boolean)
    : sorted;
  // Fiches Pro correspondant à la recherche (IA -> ids retournés ; sinon texte).
  const _pq = (search||"").trim().toLowerCase();
  const proMatches = aiActive
    ? (aiProIds||[]).map(id => pros.find(p => p.id === id)).filter(Boolean)
    : (_pq ? pros.filter(p => [p.name,p.city,p.region,p.description,(p.metiers||[]).join(" ")].filter(Boolean).join(" ").toLowerCase().includes(_pq)) : []);
  return (
    <div style={{paddingBottom:8}}>
      {showAdvanced&&<SearchAdvancedModal onClose={()=>setShowAdvanced(false)} onApply={f=>setFilters(f)}/>}

      {/* Search dark glassmorphism */}
      <div style={{padding:"14px 16px 0"}}>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <div style={{position:"relative",flex:1}}>
            <span style={{position:"absolute",left:13,top:"50%",transform:"translateY(-50%)",zIndex:1,display:"flex"}}>
              <CIcon name="search" s={15} c="var(--hc-text-faint)"/>
            </span>
            <input
              value={search}
              onChange={e=>setSearch(e.target.value)}
              onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); runAiSearch(); } }}
              placeholder={t.searchPlaceholder}
              style={{
                width:"100%",
                padding:"12px 64px 12px 36px",
                background:"var(--hc-surface-2)",
                border:"1px solid var(--hc-border)",
                borderRadius:12,
                fontSize:13.5,
                color:"var(--hc-text)",
                outline:"none",
                fontFamily:"var(--fb)",
                backdropFilter:"blur(10px)",
                transition:"border-color .2s",
              }}
              onFocus={e=>e.target.style.borderColor="var(--hc-accent)"}
              onBlur={e=>e.target.style.borderColor="var(--hc-border)"}
            />
            {search && (
              <span onClick={()=>{setSearch("");clearAiSearch();}} style={{position:"absolute",right:46,top:"50%",transform:"translateY(-50%)",cursor:"pointer",display:"flex"}}>
                <CIcon name="close" s={14} c="var(--hc-text-muted)"/>
              </span>
            )}
            <button onClick={runAiSearch} disabled={aiLoading} title={aiT.hint} style={{
              position:"absolute", right:6, top:"50%", transform:"translateY(-50%)",
              width:32, height:32, borderRadius:9, border:"none",
              background:"linear-gradient(135deg,#8B5CF6,#C8001A)",
              display:"flex", alignItems:"center", justifyContent:"center",
              cursor: aiLoading?"default":"pointer", boxShadow:"0 3px 10px rgba(124,58,237,.55)",
            }}>
              <span style={{fontSize:15,lineHeight:1}}>{aiLoading?"⏳":"✨"}</span>
            </button>
          </div>
          <motion.button
            whileTap={{ scale: 0.94 }}
            onClick={()=>onAdvanced && onAdvanced()}
            style={{
              width:44, height:44,
              background:"var(--hc-surface-2)",
              border:"1px solid var(--hc-border)",
              borderRadius:12,
              display:"flex", alignItems:"center", justifyContent:"center",
              cursor:"pointer", flexShrink:0,
              backdropFilter:"blur(10px)",
            }}
          >
            <CIcon name="filter2" s={18} c="var(--hc-text)"/>
          </motion.button>
        </div>
      </div>

      {/* Recherche IA : l'action se lance via l'étoile ✨ dans la barre ou la touche Entrée */}
      {(aiActive || aiLoading || aiMsg || (search||"").trim()) && (
        <div style={{padding:"8px 16px 0"}}>
          {aiActive ? (
            <div style={{
              background:"rgba(124,58,237,.14)", border:"1px solid rgba(168,120,255,.35)",
              borderRadius:10, padding:"8px 12px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:8,
            }}>
              <span style={{fontSize:12, fontWeight:700, color:"var(--hc-text)", fontFamily:"var(--fh)", display:"flex", alignItems:"center", gap:6}}>
                <span style={{fontSize:14}}>✨</span>{aiT.results} ({displayed.length})
              </span>
              <span onClick={clearAiSearch} style={{fontSize:11.5, fontWeight:700, color:"var(--hc-text-muted)", cursor:"pointer", whiteSpace:"nowrap"}}>✕ {aiT.clear}</span>
            </div>
          ) : aiLoading ? (
            <div style={{fontSize:12, fontWeight:700, color:"#7C3AED", fontFamily:"var(--fh)", display:"flex", alignItems:"center", gap:6}}>
              <span style={{fontSize:14}}>✨</span>{aiT.loading}
            </div>
          ) : ((search||"").trim() ? (
            <div onClick={runAiSearch} style={{fontSize:11.5, fontWeight:600, color:"#7C3AED", cursor:"pointer", display:"flex", alignItems:"center", gap:6}}>
              <span style={{fontSize:13}}>✨</span>{aiT.hint}
            </div>
          ) : null)}
          {aiMsg && <div style={{fontSize:11.5, color:"var(--hc-danger)", marginTop:6, textAlign:"center"}}>{aiMsg}</div>}
        </div>
      )}

      {/* Encart : activer les notifications (une fois, dismissable) */}
      {showPushNudge && (
        <div style={{padding:"12px 16px 0"}}>
          <div style={{
            background:"linear-gradient(135deg, rgba(200,0,26,.20) 0%, rgba(200,0,26,.08) 100%)",
            border:"1px solid rgba(255,90,100,.32)",
            borderRadius:14, padding:"12px 14px",
            display:"flex", alignItems:"center", gap:11,
            boxShadow:"0 6px 18px rgba(0,0,0,.3)",
          }}>
            <div style={{fontSize:24,flexShrink:0}}>🔔</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:800,color:"var(--hc-text)",fontFamily:"var(--fh)"}}>{PN.title}</div>
              <div style={{fontSize:11,color:"var(--hc-text-muted)",marginTop:1,lineHeight:1.35}}>{PN.desc}</div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6,flexShrink:0}}>
              <button onClick={activatePush} style={{background:"var(--app-cta-bg)",color:"#fff",border:"none",borderRadius:9,padding:"7px 14px",fontSize:12,fontWeight:800,fontFamily:"var(--fh)",cursor:"pointer",whiteSpace:"nowrap"}}>{PN.activate}</button>
              <button onClick={dismissPush} style={{background:"transparent",color:"var(--hc-text-muted)",border:"none",fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>{PN.later}</button>
            </div>
          </div>
        </div>
      )}

      {/* HorseRoad - Banner cinématique : route animée + camion qui glisse */}
      <div style={{padding:"12px 16px 0"}}>
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={()=>navigate("horseroad")}
          style={{
            width:"100%",
            background:`linear-gradient(135deg, #8B0014 0%, ${C.red} 35%, ${C.redLight} 100%)`,
            color:"#fff", border:"none", borderRadius:16,
            padding:0,
            cursor:"pointer",
            boxShadow:"0 10px 28px rgba(200,0,26,.5), inset 0 1px 0 rgba(255,255,255,.22)",
            position:"relative", overflow:"hidden",
            minHeight: 96,
            display:"block",
          }}
        >
          {/* Couche 1 : Route SVG en perspective avec lignes pointillées qui défilent */}
          <svg
            aria-hidden
            viewBox="0 0 360 100" preserveAspectRatio="none"
            style={{
              position:"absolute", inset:0, width:"100%", height:"100%",
              pointerEvents:"none",
            }}
          >
            <defs>
              <linearGradient id="hr-road" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(0,0,0,.5)"/>
                <stop offset="100%" stopColor="rgba(0,0,0,.1)"/>
              </linearGradient>
              <linearGradient id="hr-sky" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(255,180,120,.18)"/>
                <stop offset="100%" stopColor="rgba(255,180,120,0)"/>
              </linearGradient>
            </defs>
            {/* Halo coucher de soleil en haut */}
            <rect x="0" y="0" width="360" height="50" fill="url(#hr-sky)"/>
            {/* Route en perspective (trapèze) — pointe vers le centre-haut */}
            <path d="M 0,100 L 360,100 L 220,42 L 140,42 Z" fill="url(#hr-road)"/>
            {/* Lignes pointillées centrales qui défilent — l'illusion de vitesse */}
            <g>
              {[0,1,2,3].map(i => (
                <rect
                  key={i}
                  x={175} width={10}
                  y={48 + i*14} height={12}
                  rx={2}
                  fill="#fff"
                >
                  <animate
                    attributeName="y"
                    from={48 + i*14}
                    to={104 + i*14}
                    dur="1.2s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0;1;1;0"
                    keyTimes="0;.15;.85;1"
                    dur="1.2s"
                    repeatCount="indefinite"
                  />
                </rect>
              ))}
            </g>
            {/* Reflets sur les côtés de la route (bordures lumineuses) */}
            <line x1="140" y1="42" x2="0" y2="100" stroke="rgba(255,255,255,.5)" strokeWidth="1.5"/>
            <line x1="220" y1="42" x2="360" y2="100" stroke="rgba(255,255,255,.5)" strokeWidth="1.5"/>
          </svg>

          {/* Couche 2 : reflet shine qui balaie */}
          <motion.span
            aria-hidden
            initial={{ x: "-130%" }}
            animate={{ x: "230%" }}
            transition={{ duration: 1.4, repeat: Infinity, repeatDelay: 4, ease: "easeInOut" }}
            style={{
              position:"absolute", top:0, left:0, bottom:0,
              width:"55%",
              background:"linear-gradient(110deg, transparent 0%, rgba(255,255,255,.3) 50%, transparent 100%)",
              pointerEvents:"none",
              zIndex: 1,
            }}
          />

          {/* Couche 3 : Contenu textuel (titre + sous-titre + flèche) */}
          <div style={{
            position:"relative", zIndex: 3,
            display:"flex", alignItems:"center", gap:12,
            padding:"14px 16px",
          }}>
            <div style={{flex:1, textAlign:"left"}}>
              <div style={{fontFamily:"var(--fh)", fontSize:17, fontWeight:900, marginBottom:3, letterSpacing:.3}}>
                HorseRoad
              </div>
              <div style={{fontSize:11.5, opacity:.95, lineHeight:1.35, fontWeight:500}}>
                {t.transportSharing}
              </div>
            </div>
            <motion.div
              animate={{ x: [0, 4, 0] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
              style={{
                fontSize: 20, fontWeight: 800,
                flexShrink: 0,
              }}
            >→</motion.div>
          </div>
        </motion.button>
      </div>

      {/* Catégories dark chips */}
      <div style={{padding:"14px 0 4px",overflowX:"auto",display:"flex",gap:7,paddingLeft:16}}>
        {[{id:null,label:t.all},...CATEGORIES].map(c=>{
          const active=cat===c.id;
          return (
            <motion.button
              key={String(c.id)}
              whileTap={{ scale: 0.93 }}
              onClick={()=>setCat(c.id)}
              style={{
                display:"flex", alignItems:"center", gap:5,
                background: active
                  ? "var(--app-cta-bg)"
                  : "var(--hc-surface-2)",
                color: active ? "#fff" : "var(--hc-text)",
                border: `1px solid ${active ? "rgba(255,255,255,.25)" : "var(--hc-border)"}`,
                borderRadius:22,
                padding:"7px 13px",
                fontSize:11.5,
                fontFamily:"var(--fh)", fontWeight:700, letterSpacing:.3,
                whiteSpace:"nowrap", flexShrink:0,
                cursor:"pointer",
                backdropFilter: active ? "none" : "blur(10px)",
                boxShadow: active ? "0 4px 14px rgba(200,0,26,.4)" : "none",
                transition:"all .15s",
              }}
            >
              {c.id&&<CIcon name={c.id} s={13} c={active?"#fff":"var(--hc-text)"}/>}
              {c.label}
            </motion.button>
          );
        })}
        <div style={{width:8, flexShrink:0}}/>
      </div>

      {/* Titre liste */}
      <div style={{padding:"14px 16px 9px",display:"flex",justifyContent:"space-between",alignItems:"center", gap:8}}>
        <div style={{fontFamily:"var(--fh)",fontSize:18,fontWeight:900,color:"var(--hc-text)",letterSpacing:.3, flex:1, minWidth:0}}>
          <div style={{overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
            {search?`"${search}"`:cat?CATEGORIES.find(c2=>c2.id===cat)?.label:(userRegion?t.nearbyPrefix+" "+userRegion:t.recentAds)}
            <span style={{fontSize:13, fontWeight:500, color:"var(--hc-text-faint)", marginLeft:6}}>({displayed.length})</span>
          </div>
          {!search && !cat && userCountry && (
            <div style={{fontSize:10, fontWeight:600, color:"var(--hc-text-faint)", marginTop:2, letterSpacing:.5}}>
              {COUNTRIES.find(c=>c.code===userCountry)?.flag} {t.sortedByProximity}
            </div>
          )}
        </div>
        <div style={{display:"flex", gap:6, flexShrink:0}}>
          <motion.button
            whileTap={{ scale: 0.93 }}
            onClick={onAgenda}
            style={{
              background:"var(--hc-surface-2)",
              border:"1px solid var(--hc-border)",
              borderRadius:20,
              color:"var(--hc-text)",
              fontSize:11, fontFamily:"var(--fh)", fontWeight:700,
              padding:"6px 11px", cursor:"pointer",
              display:"flex", alignItems:"center", gap:4,
              backdropFilter:"blur(10px)",
            }}
          >
            <CIcon name="calendar" s={11} c="var(--hc-text)"/>{t.agenda}
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.93 }}
            onClick={onPublish}
            style={{
              background:"var(--app-cta-bg)",
              border:"none", borderRadius:20,
              color:"#fff",
              fontSize:11, fontFamily:"var(--fh)", fontWeight:700,
              padding:"6px 11px", cursor:"pointer",
              display:"flex", alignItems:"center", gap:4,
              boxShadow:"0 4px 14px rgba(200,0,26,.4)",
            }}
          >
            <CIcon name="plus" s={11} c="#fff"/>{t.publish}
          </motion.button>
        </div>
      </div>

      {/* Section Fiches Pro (recherche universelle : annonces + shop + annuaire) */}
      {proMatches.length > 0 && (
        <div style={{padding:"6px 16px 0"}}>
          <div style={{fontSize:12, fontWeight:800, color:"var(--hc-text)", fontFamily:"var(--fh)", letterSpacing:.5, marginBottom:8, display:"flex", alignItems:"center", gap:6}}>
            <CIcon name="crown" s={13} c="#7C3AED"/>{({fr:"Fiches Pro",en:"Pro listings",de:"Profi-Einträge",es:"Fichas Pro",it:"Schede Pro",pt:"Fichas Pro",nl:"Pro-fiches"}[lang]||"Fiches Pro")} ({proMatches.length})
          </div>
          <div style={{display:"flex", flexDirection:"column", gap:8}}>
            {proMatches.map(p => {
              const thumb = p.logo_url||p.cover_url||(p.photos&&p.photos[0]);
              return (
                <div key={p.id} onClick={()=>navigate("proDetail",{pro:p})} style={{
                  background:"var(--hc-surface-2)", border:"1px solid var(--hc-border)", borderRadius:12,
                  padding:10, cursor:"pointer", display:"flex", gap:10, alignItems:"center",
                }}>
                  <div style={{width:40,height:40,borderRadius:10,flexShrink:0,background:"var(--hc-accent-soft)",backgroundImage:thumb?`url(${thumb})`:"none",backgroundSize:"cover",backgroundPosition:"center",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {!thumb && <span style={{fontSize:17,fontWeight:900,color:"var(--hc-accent)",fontFamily:"var(--fh)"}}>{(p.name||"?")[0]}</span>}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13.5,fontWeight:800,color:"var(--hc-text)",fontFamily:"var(--fh)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",display:"flex",alignItems:"center",gap:5}}>
                      {p.name}{p.verified && <CIcon name="check" s={12} c="#38BDF8"/>}
                    </div>
                    <div style={{fontSize:11,color:"var(--hc-text-faint)",marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{[p.city,p.region].filter(Boolean).join(", ")}</div>
                  </div>
                  <CIcon name="chevron" s={15} c="var(--hc-text-faint)"/>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{padding:"12px 16px 0",display:"flex",flexDirection:"column",gap:10}}>
        {displayed.length===0
          ? (proMatches.length>0 ? null : <div style={{textAlign:"center",padding:"50px 20px",color:"var(--hc-text-muted)"}}>
              <div style={{fontSize:42, marginBottom:14}}>🐴</div>
              <div style={{fontSize:16, fontWeight:700, color:"var(--hc-text)", marginBottom:6, fontFamily:"var(--fh)"}}>{aiActive ? aiT.none : t.noAdsYet}</div>
              {!aiActive && <div style={{fontSize:13, lineHeight:1.6}}>{t.beFirst}</div>}
            </div>)
          : displayed.map(ad=><AdCard key={ad.id} ad={ad} onClick={()=>onAd(ad)} lang={lang}/>)
        }
      </div>
    </div>
  );
}

export function TabAnnonces({onAd,onPublish,ads=[],lang="fr"}) {
  const t = useLang(lang);
  const [f,setF]=useState("all");
  const fl=[{id:"all",label:t.all},...CATEGORIES];
  const [userPos,setUserPos]=useState(null);
  useEffect(()=>{ let on=true; getKnownPosition().then(p=>{ if(on&&p) setUserPos(p); }); return ()=>{on=false}; },[]);
  const adsNoGear = ads.filter(a => a.cat !== "gear" && a.category !== "gear");
  let filtered=f==="all"?adsNoGear:adsNoGear.filter(a=>a.cat===f);
  if (userPos) { try { filtered=[...filtered].sort((a,b)=>compareByDistance(a,b,userPos)); } catch(e){} }
  return (
    <div style={{paddingBottom:8}}>
      {/* Bandeau SEO Google : motive à publier — autonome (lisible dans les 2 thèmes), visible visiteurs non connectés */}
      <div style={{padding:"14px 16px 2px"}}>
        <div style={{background:"#ECFDF5",border:"1px solid #6EE7B7",borderRadius:11,padding:"11px 13px",display:"flex",alignItems:"center",gap:10}}>
          <CIcon name="globe" s={18} c="#059669"/>
          <div style={{fontSize:12,color:"#065f46",lineHeight:1.45,fontWeight:600}}>{t.seoPublishBanner}</div>
        </div>
      </div>

      {/* Chips catégories dark */}
      <div style={{padding:"14px 0 4px",overflowX:"auto",display:"flex",gap:7,paddingLeft:16}}>
        {fl.map(fi=>{
          const active=f===fi.id;
          return (
            <motion.button
              key={fi.id}
              whileTap={{ scale: 0.93 }}
              onClick={()=>setF(fi.id)}
              style={{
                display:"flex", alignItems:"center", gap:5,
                background: active
                  ? "var(--app-cta-bg)"
                  : "var(--hc-surface-2)",
                color: active ? "#fff" : "var(--hc-text)",
                border: `1px solid ${active ? "rgba(255,255,255,.25)" : "var(--hc-border)"}`,
                borderRadius:22, padding:"7px 13px",
                fontSize:11.5,
                fontFamily:"var(--fh)", fontWeight:700, letterSpacing:.3,
                whiteSpace:"nowrap", flexShrink:0,
                cursor:"pointer",
                backdropFilter: active ? "none" : "blur(10px)",
                boxShadow: active ? "0 4px 14px rgba(200,0,26,.4)" : "none",
                transition:"all .15s",
              }}
            >
              {fi.id!=="all"&&<CIcon name={fi.id} s={12} c={active?"#fff":"var(--hc-text)"}/>}
              {fi.label}
            </motion.button>
          );
        })}
        <div style={{width:8, flexShrink:0}}/>
      </div>

      {/* CTA Publier — gradient rouge + shine */}
      <div style={{padding:"10px 16px 10px"}}>
        <PrimaryButton onClick={onPublish} sx={{padding:"14px 16px"}}>
          <span style={{display:"inline-flex", alignItems:"center", gap:8}}>
            <CIcon name="plus" s={14} c="#fff"/>{t.publishAd}
          </span>
        </PrimaryButton>
      </div>

      {/* Compteur résultats */}
      <div style={{padding:"0 16px",display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <span style={{fontFamily:"var(--fh)", fontSize:16, fontWeight:800, color:"var(--hc-text)", letterSpacing:.3}}>
          {filtered.length} <span style={{color:"var(--hc-text-faint)", fontWeight:500}}>{filtered.length>1?t.adPlural:t.adSingular}</span>
        </span>
      </div>

      <div style={{padding:"0 16px",display:"flex",flexDirection:"column",gap:10}}>
        {filtered.length === 0 ? (
          <div style={{textAlign:"center", padding:"50px 20px", color:"var(--hc-text-muted)"}}>
            <div style={{fontSize:42, marginBottom:14}}>🐎</div>
            <div style={{fontSize:16, fontWeight:700, color:"var(--hc-text)", marginBottom:6, fontFamily:"var(--fh)"}}>{t.noAdsYet}</div>
            <div style={{fontSize:13, lineHeight:1.6}}>{t.beFirst}</div>
          </div>
        ) : (
          filtered.map(ad=><AdCard key={ad.id} ad={ad} onClick={()=>onAd(ad)} lang={lang}/>)
        )}
      </div>
    </div>
  );
}

export function TabMarketplace({onItem,onPublish,ads=[],lang="fr",user={}}) {
  const t = useLang(lang);
  const [cat,setCat]=useState("all"); const [sort,setSort]=useState("recent"); const [search,setSearch]=useState("");
  const [userPos,setUserPos]=useState(null);
  useEffect(()=>{ let on=true; getKnownPosition().then(p=>{ if(on&&p) setUserPos(p); }); return ()=>{on=false}; },[]);
  // Filtrer sur les vraies annonces avec category='gear'
  const gearAds = ads.filter(a => a.cat === "gear" || a.category === "gear");
  const filtered=gearAds.filter(a=>{
    // Les annonces publiees avant l'ajout du rayon n'en ont pas : on les range
    // dans "Autres" plutot que de les rendre introuvables des qu'on filtre.
    const matchC=cat==="all"||(a.subcat||"autres")===cat;
    const q=search.toLowerCase();
    const trTitre=(a.translations&&a.translations[lang]&&a.translations[lang].title)||"";
    const matchS=!q||(a.title||"").toLowerCase().includes(q)||trTitre.toLowerCase().includes(q)||(a.loc||a.location||"").toLowerCase().includes(q);
    return matchC&&matchS;
  }).sort((a,b)=>{
    const pA=parseInt(String(a.price).replace(/[^0-9]/g,''))||0;
    const pB=parseInt(String(b.price).replace(/[^0-9]/g,''))||0;
    if (sort==="price_asc") return pA-pB;
    if (sort==="price_desc") return pB-pA;
    // "Récent" : le plus proche d'abord quand la position est connue
    return userPos ? compareByDistance(a,b,userPos) : 0;
  });

  // Recherche IA (materiel / marketplace). i18n inline.
  const AIS_I18N = {
    fr:{hint:"Aide ta recherche avec l'IA",loading:"L'IA cherche…",results:"Résultats IA",clear:"Effacer",error:"Recherche IA indisponible, réessaie.",needQuery:"Écris d'abord ta recherche ci-dessus.",login:"Connecte-toi pour utiliser la recherche IA.",none:"Aucun article ne correspond à ta demande."},
    en:{hint:"Get AI help with your search",loading:"AI is searching…",results:"AI results",clear:"Clear",error:"AI search unavailable, try again.",needQuery:"Type your search above first.",login:"Log in to use AI search.",none:"No item matches your request."},
    de:{hint:"Suche mit KI-Hilfe",loading:"KI sucht…",results:"KI-Ergebnisse",clear:"Löschen",error:"KI-Suche nicht verfügbar, versuche es erneut.",needQuery:"Gib oben zuerst deine Suche ein.",login:"Melde dich an, um die KI-Suche zu nutzen.",none:"Kein Artikel passt zu deiner Anfrage."},
    it:{hint:"Fatti aiutare dall'IA nella ricerca",loading:"L'IA sta cercando…",results:"Risultati IA",clear:"Cancella",error:"Ricerca IA non disponibile, riprova.",needQuery:"Scrivi prima la tua ricerca qui sopra.",login:"Accedi per usare la ricerca IA.",none:"Nessun articolo corrisponde alla tua richiesta."},
    es:{hint:"Deja que la IA te ayude a buscar",loading:"La IA está buscando…",results:"Resultados IA",clear:"Borrar",error:"Búsqueda IA no disponible, inténtalo de nuevo.",needQuery:"Escribe primero tu búsqueda arriba.",login:"Inicia sesión para usar la búsqueda IA.",none:"Ningún artículo coincide con tu búsqueda."},
    pt:{hint:"Deixa a IA ajudar na tua pesquisa",loading:"A IA está a procurar…",results:"Resultados IA",clear:"Limpar",error:"Pesquisa IA indisponível, tenta de novo.",needQuery:"Escreve primeiro a tua pesquisa acima.",login:"Inicia sessão para usar a pesquisa IA.",none:"Nenhum artigo corresponde ao teu pedido."},
    nl:{hint:"Laat de AI je zoekopdracht helpen",loading:"De AI zoekt…",results:"AI-resultaten",clear:"Wissen",error:"AI-zoekfunctie niet beschikbaar, probeer opnieuw.",needQuery:"Typ eerst je zoekopdracht hierboven.",login:"Meld je aan om de AI-zoekfunctie te gebruiken.",none:"Geen enkel artikel komt overeen met je vraag."},
  };
  const aiT = AIS_I18N[lang] || AIS_I18N.fr;
  const [aiLoading,setAiLoading]=useState(false);
  const [aiIds,setAiIds]=useState(null);
  const [aiMsg,setAiMsg]=useState("");
  const runAiSearch = async () => {
    const q = (search||"").trim();
    setAiMsg("");
    if (!q) { setAiMsg(aiT.needQuery); return; }
    if (!user?.id) { setAiMsg(aiT.login); return; }
    setAiLoading(true);
    try {
      const pool = gearAds.slice(0,120).map(a => ({ id:a.id, cat:a.cat, title:(a.translations&&a.translations[lang]&&a.translations[lang].title)||a.title, price:a.price, region:a.region, city:a.city, loc:a.loc||a.location, desc:(a.translations&&a.translations[lang]&&a.translations[lang].description)||a.desc }));
      const { data, error } = await supabase.functions.invoke("ai-search", { body: { query:q, ads:pool, lang } });
      if (error || !data || data.error || !Array.isArray(data.ids)) { setAiMsg(aiT.error); }
      else { setAiIds(data.ids); if (data.ids.length===0) setAiMsg(aiT.none); }
    } catch(e) { setAiMsg(aiT.error); }
    setAiLoading(false);
  };
  const clearAiSearch = () => { setAiIds(null); setAiMsg(""); };
  const aiActive = aiIds !== null;
  const displayed = aiActive ? aiIds.map(id => gearAds.find(a => a.id === id)).filter(Boolean) : filtered;
  return (
    <div style={{paddingBottom:8}}>
      {/* Hero Marketplace — gradient violet/rouge avec halo */}
      <div style={{
        background: `linear-gradient(135deg, rgba(124,58,237,.25) 0%, rgba(200,0,26,.18) 100%), var(--hc-surface)`,
        backdropFilter: "blur(10px)",
        borderBottom: `1px solid var(--hc-border)`,
        padding: "16px 16px 18px",
        position: "relative", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", top: -30, right: -30,
          width: 140, height: 140, borderRadius: "50%",
          background: `radial-gradient(circle, rgba(200,0,26,.35) 0%, transparent 70%)`,
          filter: "blur(25px)", pointerEvents: "none",
        }}/>
        <div style={{position:"relative", zIndex:1}}>
          <div style={{fontFamily:"var(--fh)", fontSize:11, fontWeight:700, color:"var(--hc-text-muted)", letterSpacing:2.5, textTransform:"uppercase", marginBottom:3}}>HorseConnex</div>
          <div style={{fontFamily:"var(--fh)", fontSize:28, fontWeight:900, color:"var(--hc-text)", letterSpacing:.5, marginBottom:14}}>MARKETPLACE</div>
          <div style={{position:"relative"}}>
            <span style={{position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", zIndex:1, display:"flex"}}>
              <CIcon name="search" s={15} c="var(--hc-text-faint)"/>
            </span>
            <input
              value={search}
              onChange={e=>setSearch(e.target.value)}
              onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); runAiSearch(); } }}
              placeholder={t.equipSearchPh}
              style={{
                width:"100%",
                padding:"11px 64px 11px 36px",
                background:"var(--hc-surface-2)",
                border:"1px solid var(--hc-border)",
                borderRadius:12,
                fontSize:13, color:"var(--hc-text)",
                outline:"none", fontFamily:"var(--fb)",
                backdropFilter:"blur(10px)",
              }}
            />
            {search && (
              <span onClick={()=>{setSearch("");clearAiSearch();}} style={{position:"absolute", right:46, top:"50%", transform:"translateY(-50%)", cursor:"pointer", display:"flex"}}>
                <CIcon name="close" s={13} c="var(--hc-text-muted)"/>
              </span>
            )}
            <button onClick={runAiSearch} disabled={aiLoading} title={aiT.hint} style={{
              position:"absolute", right:6, top:"50%", transform:"translateY(-50%)",
              width:32, height:32, borderRadius:9, border:"none",
              background:"linear-gradient(135deg,#8B5CF6,#C8001A)",
              display:"flex", alignItems:"center", justifyContent:"center",
              cursor: aiLoading?"default":"pointer", boxShadow:"0 3px 10px rgba(124,58,237,.55)",
            }}>
              <span style={{fontSize:15,lineHeight:1}}>{aiLoading?"⏳":"✨"}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Recherche IA : l'action se lance via l'étoile ✨ dans la barre ou la touche Entrée */}
      {(aiActive || aiLoading || aiMsg || (search||"").trim()) && (
        <div style={{padding:"10px 16px 0"}}>
          {aiActive ? (
            <div style={{
              background:"rgba(124,58,237,.14)", border:"1px solid rgba(168,120,255,.35)",
              borderRadius:10, padding:"8px 12px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:8,
            }}>
              <span style={{fontSize:12, fontWeight:700, color:"var(--hc-text)", fontFamily:"var(--fh)", display:"flex", alignItems:"center", gap:6}}>
                <span style={{fontSize:14}}>✨</span>{aiT.results} ({displayed.length})
              </span>
              <span onClick={clearAiSearch} style={{fontSize:11.5, fontWeight:700, color:"var(--hc-text-muted)", cursor:"pointer", whiteSpace:"nowrap"}}>✕ {aiT.clear}</span>
            </div>
          ) : aiLoading ? (
            <div style={{fontSize:12, fontWeight:700, color:"#7C3AED", fontFamily:"var(--fh)", display:"flex", alignItems:"center", gap:6}}>
              <span style={{fontSize:14}}>✨</span>{aiT.loading}
            </div>
          ) : ((search||"").trim() ? (
            <div onClick={runAiSearch} style={{fontSize:11.5, fontWeight:600, color:"#7C3AED", cursor:"pointer", display:"flex", alignItems:"center", gap:6}}>
              <span style={{fontSize:13}}>✨</span>{aiT.hint}
            </div>
          ) : null)}
          {aiMsg && <div style={{fontSize:11.5, color:"var(--hc-danger)", marginTop:6, textAlign:"center"}}>{aiMsg}</div>}
        </div>
      )}

      {/* Bandeau SEO Google : motive à publier — autonome (lisible dans les 2 thèmes), visible visiteurs non connectés */}
      <div style={{padding:"12px 16px 2px"}}>
        <div style={{background:"#ECFDF5",border:"1px solid #6EE7B7",borderRadius:11,padding:"11px 13px",display:"flex",alignItems:"center",gap:10}}>
          <CIcon name="globe" s={18} c="#059669"/>
          <div style={{fontSize:12,color:"#065f46",lineHeight:1.45,fontWeight:600}}>{t.seoPublishBanner}</div>
        </div>
      </div>

      {/* Sous-catégories : pill scrollables dark */}
      <div style={{
        padding:"10px 0 4px", overflowX:"auto",
        display:"flex", gap:8, paddingLeft:16,
        borderBottom:"1px solid var(--hc-border)",
      }}>
        {MSUBCATS.map(c=>{
          const active=cat===c.id;
          return (
            <motion.button
              key={c.id}
              whileTap={{ scale: 0.93 }}
              onClick={()=>setCat(c.id)}
              style={{
                display:"flex", flexDirection:"column", alignItems:"center", gap:4,
                padding:"4px 13px 10px",
                background:"none", border:"none",
                borderBottom: active ? `2.5px solid var(--hc-accent)` : "2.5px solid transparent",
                cursor:"pointer", flexShrink:0,
                transition:"all .15s", minWidth:62,
              }}
            >
              <div style={{
                width:34, height:34, borderRadius:10,
                background: active ? "var(--hc-accent-soft)" : "var(--hc-surface-2)",
                border: `1px solid ${active ? "var(--hc-accent-soft)" : "var(--hc-border)"}`,
                display:"flex", alignItems:"center", justifyContent:"center",
                transition:"background .15s",
              }}>
                <CIcon name="gear" s={15} c={active ? "var(--hc-accent)" : "var(--hc-text-faint)"}/>
              </div>
              <span style={{
                fontSize:9,
                fontFamily:"var(--fh)", fontWeight:700, letterSpacing:.4,
                color: active ? "var(--hc-accent)" : "var(--hc-text-faint)",
                textTransform:"uppercase", whiteSpace:"nowrap",
              }}>{(c.i18n && (c.i18n[lang] || c.i18n.fr)) || c.label}</span>
            </motion.button>
          );
        })}
        <div style={{width:8, flexShrink:0}}/>
      </div>

      {/* Compteur + tri */}
      <div style={{padding:"11px 16px 7px", display:"flex", justifyContent:"space-between", alignItems:"center"}}>
        <span style={{fontSize:12, color:"var(--hc-text-faint)"}}>
          <span style={{fontWeight:800, color:"var(--hc-text)", fontFamily:"var(--fh)"}}>{displayed.length}</span> {displayed.length>1?t.resultPlural:t.resultSingular}
        </span>
        <div style={{display:"flex", gap:5}}>
          {[["recent",t.recent],["price_asc",t.priceAsc],["price_desc",t.priceDesc]].map(([v,l])=>(
            <motion.button
              key={v}
              whileTap={{ scale: 0.92 }}
              onClick={()=>setSort(v)}
              style={{
                background: sort===v
                  ? "var(--app-cta-bg)"
                  : "var(--hc-surface-2)",
                color: sort===v ? "#fff" : "var(--hc-text-muted)",
                border: `1px solid ${sort===v ? "rgba(255,255,255,.25)" : "var(--hc-border)"}`,
                borderRadius:20, padding:"4px 10px",
                fontSize:10.5, fontFamily:"var(--fh)", fontWeight:700,
                cursor:"pointer",
                backdropFilter: sort===v ? "none" : "blur(10px)",
                transition:"all .15s",
              }}
            >{l}</motion.button>
          ))}
        </div>
      </div>

      {/* CTA Publier */}
      <div style={{padding:"10px 16px 10px"}}>
        <PrimaryButton onClick={onPublish} sx={{padding:"12px 16px", fontSize:14, borderRadius:12}}>
          <span style={{display:"inline-flex", alignItems:"center", gap:8}}>
            <CIcon name="plus" s={14} c="#fff"/>{t.sellMaterial}
          </span>
        </PrimaryButton>
      </div>

      {/* Grille des items */}
      <div style={{padding:"0 16px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        {displayed.length===0 ? (
          <div style={{gridColumn:"1/-1", textAlign:"center", padding:"50px 16px", color:"var(--hc-text-muted)"}}>
            <div style={{fontSize:42, marginBottom:14}}>🛒</div>
            <div style={{fontSize:16, fontWeight:700, color:"var(--hc-text)", marginBottom:6, fontFamily:"var(--fh)"}}>{aiActive ? aiT.none : t.noItemsYet}</div>
            {!aiActive && <div style={{fontSize:13, lineHeight:1.6}}>{t.beFirstSell}</div>}
          </div>
        ) : (
          displayed.map(item=>(
            <motion.div
              key={item.id}
              whileTap={{ scale: 0.97 }}
              onClick={()=>onItem(item)}
              style={{
                background:"var(--hc-surface)",
                backdropFilter:"blur(14px)",
                border:"1px solid var(--hc-border)",
                borderRadius:13, overflow:"hidden",
                cursor:"pointer",
                boxShadow:"0 8px 24px rgba(0,0,0,.14), 0 2px 6px rgba(0,0,0,.10)",
              }}
            >
              <div style={{height:106, background: item.photos?.[0] ? "transparent" : "var(--hc-surface-2)", display:"flex", alignItems:"center", justifyContent:"center", position:"relative", overflow:"hidden"}}>
                {item.photos?.[0] ? (
                  <img src={item.photos[0]} alt={item.title} style={{width:"100%", height:"100%", objectFit:"cover"}}/>
                ) : (
                  <CIcon name="gear" s={34} c="var(--hc-text-faint)"/>
                )}
                {item.badge && <div style={{position:"absolute", top:7, left:7, background: item.badge==="TOP" ? "linear-gradient(135deg,#F5A623,#C4690A)" : item.badge==="NEW" ? "linear-gradient(135deg,#22c55e,#047857)" : "rgba(13,13,26,.85)", color:"#fff", fontSize:8, fontWeight:800, padding:"2px 6px", borderRadius:20, fontFamily:"var(--fh)", boxShadow:"0 2px 6px rgba(0,0,0,.4)"}}>{item.badge}</div>}
                {item.cond && <div style={{position:"absolute", bottom:7, right:7, background:COND_COL[item.cond]||"#555", color:"#fff", fontSize:8, fontWeight:700, padding:"2px 6px", borderRadius:20, fontFamily:"var(--fh)"}}>{item.cond}</div>}
              </div>
              <div style={{padding:"9px 10px 10px"}}>
                <div style={{fontSize:12, fontWeight:700, color:"var(--hc-text)", fontFamily:"var(--fh)", lineHeight:1.3, marginBottom:4, overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical"}}>{(item.translations && item.translations[lang] && item.translations[lang].title) || item.title}</div>
                <div style={{fontSize:15, fontWeight:900, color:"var(--hc-accent)", fontFamily:"var(--fh)"}}>{item.price}</div>
                <div style={{fontSize:10, color:"var(--hc-text-faint)", marginTop:3, display:"flex", alignItems:"center", gap:2}}>
                  <CIcon name="pin" s={9} c="var(--hc-text-faint)"/>{item.loc || item.location || (item.city ? item.city : "")}
                  {item.rating && <span style={{marginLeft:"auto", display:"flex", alignItems:"center", gap:2, color:"#FCD34D"}}><CIcon name="star" s={9} c="#FCD34D"/>{item.rating}</span>}
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
      <div style={{height:16}}/>
    </div>
  );
}

export function TabCarte({ads=[],onAd,lang="fr"}) {
  const t = useLang(lang);
  const [catF,setCatF]=useState("all");
  const iRef=useRef(null);
  const [mapReady,setMapReady]=useState(false);
  const [mapFailed,setMapFailed]=useState(false);
  const [reloadKey,setReloadKey]=useState(0);
  const [geocoded,setGeocoded]=useState({});  // id -> [lat,lng] geocodes a la volee

  // Repli : si la carte (CDN Leaflet) ne signale pas "ready" sous 9s, on propose
  // de réessayer (réseau lent / CDN indispo) plutôt que de laisser un écran vide.
  useEffect(() => {
    if (mapReady) return;
    const t = setTimeout(() => setMapFailed(true), 9000);
    return () => clearTimeout(t);
  }, [mapReady, reloadKey]);
  const retryMap = () => { setMapFailed(false); setMapReady(false); setReloadKey(k => k + 1); };
  
  // Geocoder les annonces qui ont city/region mais pas lat/lng
  useEffect(() => {
    const todo = ads.filter(a => !a.hideFromMap && !a.lat && !a.lng && a.city && !geocoded[a.id]);
    if (todo.length === 0) return;
    
    let cancelled = false;
    (async () => {
      for (const ad of todo) {
        if (cancelled) return;
        try {
          const country = ad.country === 'CH' ? 'Switzerland' : ad.country === 'FR' ? 'France' : ad.country;
          const coords = await geocodeCity(ad.city, ad.region, country);
          if (coords && !cancelled) {
            setGeocoded(prev => ({...prev, [ad.id]: coords}));
            // Sauver dans la BD pour les prochaines fois
            try {
              await supabase.from('ads').update({ lat: coords[0], lng: coords[1] }).eq('id', ad.id);
            } catch(e) {}
            // Petite pause pour ne pas spammer Nominatim (1 req/sec max)
            await new Promise(r => setTimeout(r, 1100));
          }
        } catch(e) {
          console.error('Geocoding failed for', ad.id, e);
        }
      }
    })();
    
    return () => { cancelled = true; };
  }, [ads]);
  
  // Filtrer les annonces avec coords (avec protection)
  // On exclut les annonces dont le vendeur a coché "ne pas afficher sur la carte"
  let adsWithCoords = [];
  try {
    adsWithCoords = ads.filter(a => !a.hideFromMap).map(a => {
      try {
        // Prioriser les coords geocodees si pas dans la BD
        const adWithCoords = geocoded[a.id] ? {...a, lat: geocoded[a.id][0], lng: geocoded[a.id][1]} : a;
        return {...adWithCoords, coords: getAdCoords(adWithCoords)};
      } catch(e) {
        return {...a, coords: null};
      }
    }).filter(a => a.coords);
  } catch(err) {
    console.error("Map coords error:", err);
    adsWithCoords = [];
  }
  const filteredAds = catF === "all" ? adsWithCoords : adsWithCoords.filter(a => a.cat === catF);
  
  // Injecter les annonces dans la carte quand elle est prête
  useEffect(() => {
    if (!mapReady || !iRef.current?.contentWindow) return;
    try {
      const markers = filteredAds.map(a => ({
        id: a.id,
        title: a.title,
        cat: a.cat,
        loc: a.loc,
        price: a.price,
        coords: a.coords,
        photo: a.photos?.[0] || ""
      }));
      iRef.current.contentWindow.updateMarkers(JSON.stringify(markers));
    } catch(e) { console.error(e); }
  }, [filteredAds, mapReady]);
  
  const sendFilter=cat=>{
    setCatF(cat);
    if(iRef.current?.contentWindow){try{iRef.current.contentWindow.filterMap(cat);}catch(e){}}
  };
  
  // Listener pour recevoir clicks depuis l'iframe
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === "horseconnex_ad_click" && e.data.adId) {
        const ad = ads.find(a => a.id === e.data.adId);
        if (ad && onAd) onAd(ad);
      }
      if (e.data?.type === "horseconnex_map_ready") {
        setMapReady(true);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [ads, onAd]);
  return (
    <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 130px)"}}>
      {/* Chips catégories de la carte — glassmorphism dark */}
      <div style={{
        background:"rgba(13,13,26,.55)",
        backdropFilter:"blur(14px)",
        borderBottom:`1px solid ${C.glassBorder}`,
        padding:"9px 0", overflowX:"auto",
        display:"flex", gap:7, paddingLeft:14, flexShrink:0,
      }}>
        {[{id:"all",label:t.all},...CATEGORIES,{id:"gear",label:t.marketplaceTab}].map(c=>{
          const active=catF===c.id;
          const col=CAT_COLOR[c.id]||C.red;
          return (
            <motion.button
              key={String(c.id)}
              whileTap={{ scale: 0.93 }}
              onClick={()=>sendFilter(c.id)}
              style={{
                display:"flex", alignItems:"center", gap:5,
                background: active
                  ? `linear-gradient(135deg, ${col} 0%, ${col}cc 100%)`
                  : "rgba(255,255,255,.06)",
                color: active ? "#fff" : "rgba(255,255,255,.75)",
                border: `1px solid ${active ? "rgba(255,255,255,.25)" : "rgba(255,255,255,.12)"}`,
                borderRadius:20, padding:"6px 12px",
                fontSize:10.5,
                fontFamily:"var(--fh)", fontWeight:700, letterSpacing:.3,
                whiteSpace:"nowrap", flexShrink:0,
                cursor:"pointer",
                backdropFilter: active ? "none" : "blur(10px)",
                boxShadow: active ? `0 4px 14px ${col}66` : "none",
                transition:"all .15s",
              }}
            >
              {c.id&&<CIcon name={c.id} s={12} c={active?"#fff":"rgba(255,255,255,.75)"}/>}
              {c.label}
            </motion.button>
          );
        })}
        <div style={{width:8, flexShrink:0}}/>
      </div>
      <div style={{flex:1,overflow:"hidden",position:"relative"}}>
        <iframe key={reloadKey} ref={iRef} srcDoc={MAP_HTML} style={{width:"100%",height:"100%",border:"none",display:"block"}} title="HorseConnex Map" allow="geolocation" sandbox="allow-scripts allow-same-origin"/>
        {!mapReady && !mapFailed && (
          <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12,background:"rgba(13,13,26,.82)",backdropFilter:"blur(6px)",pointerEvents:"none",zIndex:5}}>
            <motion.div animate={{rotate:360}} transition={{duration:0.8,repeat:Infinity,ease:"linear"}}
              style={{width:32,height:32,border:"3px solid rgba(255,255,255,.18)",borderTopColor:C.red,borderRadius:"50%"}}/>
            <div style={{color:"rgba(255,255,255,.75)",fontSize:12,fontWeight:600}}>Chargement de la carte…</div>
          </div>
        )}
        {mapFailed && !mapReady && (
          <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,padding:24,textAlign:"center",background:"rgba(13,13,26,.93)",zIndex:6}}>
            <div style={{fontSize:34}}>🗺️</div>
            <div style={{color:"#fff",fontSize:14,fontWeight:700,fontFamily:"var(--fh)"}}>{t.dMapUnavailable||"Carte indisponible"}</div>
            <div style={{color:"rgba(255,255,255,.6)",fontSize:12,lineHeight:1.5,maxWidth:240}}>Vérifie ta connexion internet et réessaie.</div>
            <button onClick={retryMap} style={{background:"var(--app-cta-bg)",color:"#fff",border:"none",borderRadius:10,padding:"9px 22px",fontSize:13,fontWeight:700,fontFamily:"var(--fh)",cursor:"pointer",boxShadow:"0 4px 14px rgba(200,0,26,.4)"}}>↻ Réessayer</button>
          </div>
        )}
        <button
          onClick={() => {
            if (!navigator.geolocation) {
              alert("Geolocalisation non supportee par votre navigateur");
              return;
            }
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                if (iRef.current?.contentWindow?.goToLocation) {
                  iRef.current.contentWindow.goToLocation(pos.coords.latitude, pos.coords.longitude, 13);
                }
              },
              (err) => {
                let msg = "Impossible de recuperer votre position";
                if (err.code === 1) msg = "Geolocalisation refusee. Activez-la dans les parametres.";
                else if (err.code === 2) msg = "Position indisponible. Verifiez votre GPS.";
                else if (err.code === 3) msg = "Delai de geolocalisation expire.";
                alert(msg);
              },
              { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
            );
          }}
          style={{
            position: "absolute",
            bottom: 16,
            right: 16,
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: "#fff",
            border: "none",
            boxShadow: "0 2px 8px rgba(0,0,0,.2)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 22,
            zIndex: 10
          }}
          aria-label={t.myPosition}
          title={t.centerMyPosition}
        >📍</button>
      </div>
      <div style={{
        background:"rgba(13,13,26,.7)",
        backdropFilter:"blur(14px)",
        borderTop:`1px solid ${C.glassBorder}`,
        padding:"8px 16px",
        display:"flex", justifyContent:"space-between", alignItems:"center",
        flexShrink:0,
      }}>
        <span style={{fontSize:11, color:"rgba(255,255,255,.6)"}}>
          <span style={{fontWeight:800, color:"#fff", fontFamily:"var(--fh)"}}>{filteredAds.length}</span> {t.adsOnMap}
        </span>
        <span style={{fontSize:10, color:"rgba(255,255,255,.45)", display:"flex", alignItems:"center", gap:3}}>
          <CIcon name="globe" s={10} c="rgba(255,255,255,.45)"/>OpenStreetMap
        </span>
      </div>
    </div>
  );
}

// ============================================================
//  MapScreen — carte PLEIN ÉCRAN (annonces + fiches Pro) avec
//  barre de recherche IA en haut + flèche retour. Pas de bottom nav
//  (route dédiée dans App.jsx). Réutilise l'iframe MAP_HTML.
// ============================================================
const MAP_FAM = { hebergement:"ecurie", sante:"care", enseignement:"lessons", elevage:"horses", transport:"transport", equipement:"rental", agricole:"jobs", services:"photo", institutions:"events" };
const MAP_I18N = {
  fr:{ title:"Carte", search:"Rechercher un cheval, une écurie, un pro…", ai:"Aide IA", loading:"L'IA cherche…", results:"Résultats", clear:"Effacer", error:"Recherche IA indisponible.", login:"Connecte-toi pour la recherche IA.", none:"Rien trouvé pour cette recherche.", mapLoading:"Chargement de la carte…", mapDown:"Carte indisponible", mapDownSub:"Vérifie ta connexion et réessaie.", retry:"Réessayer", count:"sur la carte", searchPros:"Rechercher un pro (nom, ville, métier…)", kAll:"Tout", kAds:"Annonces", kShop:"Shop", kPros:"Pro", kRoad:"HorseRoad" },
  en:{ title:"Map", search:"Search a horse, a stable, a pro…", ai:"AI help", loading:"AI is searching…", results:"Results", clear:"Clear", error:"AI search unavailable.", login:"Log in for AI search.", none:"Nothing found for this search.", mapLoading:"Loading the map…", mapDown:"Map unavailable", mapDownSub:"Check your connection and retry.", retry:"Retry", count:"on the map", searchPros:"Search a pro (name, city, trade…)", kAll:"All", kAds:"Listings", kShop:"Shop", kPros:"Pros", kRoad:"HorseRoad" },
  de:{ title:"Karte", search:"Pferd, Stall, Profi suchen…", ai:"KI-Hilfe", loading:"KI sucht…", results:"Ergebnisse", clear:"Löschen", error:"KI-Suche nicht verfügbar.", login:"Zur KI-Suche anmelden.", none:"Nichts gefunden.", mapLoading:"Karte wird geladen…", mapDown:"Karte nicht verfügbar", mapDownSub:"Verbindung prüfen und erneut versuchen.", retry:"Erneut", count:"auf der Karte", searchPros:"Profi suchen (Name, Ort, Beruf…)", kAll:"Alle", kAds:"Anzeigen", kShop:"Shop", kPros:"Profis", kRoad:"HorseRoad" },
  es:{ title:"Mapa", search:"Busca un caballo, una cuadra, un pro…", ai:"Ayuda IA", loading:"La IA busca…", results:"Resultados", clear:"Borrar", error:"Búsqueda IA no disponible.", login:"Inicia sesión para la búsqueda IA.", none:"No se encontró nada.", mapLoading:"Cargando el mapa…", mapDown:"Mapa no disponible", mapDownSub:"Comprueba tu conexión y reintenta.", retry:"Reintentar", count:"en el mapa", searchPros:"Buscar un pro (nombre, ciudad, oficio…)", kAll:"Todo", kAds:"Anuncios", kShop:"Tienda", kPros:"Pros", kRoad:"HorseRoad" },
  it:{ title:"Mappa", search:"Cerca un cavallo, una scuderia, un pro…", ai:"Aiuto IA", loading:"L'IA sta cercando…", results:"Risultati", clear:"Cancella", error:"Ricerca IA non disponibile.", login:"Accedi per la ricerca IA.", none:"Nessun risultato.", mapLoading:"Caricamento della mappa…", mapDown:"Mappa non disponibile", mapDownSub:"Controlla la connessione e riprova.", retry:"Riprova", count:"sulla mappa", searchPros:"Cerca un pro (nome, città, mestiere…)", kAll:"Tutto", kAds:"Annunci", kShop:"Shop", kPros:"Pro", kRoad:"HorseRoad" },
  pt:{ title:"Mapa", search:"Procura um cavalo, uma cavalariça, um pro…", ai:"Ajuda IA", loading:"A IA procura…", results:"Resultados", clear:"Limpar", error:"Pesquisa IA indisponível.", login:"Inicia sessão para a pesquisa IA.", none:"Nada encontrado.", mapLoading:"A carregar o mapa…", mapDown:"Mapa indisponível", mapDownSub:"Verifica a ligação e tenta de novo.", retry:"Tentar de novo", count:"no mapa", searchPros:"Procurar um pro (nome, cidade, ofício…)", kAll:"Tudo", kAds:"Anúncios", kShop:"Loja", kPros:"Profis", kRoad:"HorseRoad" },
  nl:{ title:"Kaart", search:"Zoek een paard, een stal, een pro…", ai:"AI-hulp", loading:"De AI zoekt…", results:"Resultaten", clear:"Wissen", error:"AI-zoekfunctie niet beschikbaar.", login:"Meld je aan voor de AI-zoekfunctie.", none:"Niets gevonden.", mapLoading:"Kaart laden…", mapDown:"Kaart niet beschikbaar", mapDownSub:"Controleer je verbinding en probeer opnieuw.", retry:"Opnieuw", count:"op de kaart", searchPros:"Zoek een pro (naam, gemeente, beroep…)", kAll:"Alles", kAds:"Advertenties", kShop:"Shop", kPros:"Pro's", kRoad:"HorseRoad" }
};

// mode : "all" (annonces + pros) ou "pros" (annuaire seul, ouvert depuis l'onglet Pro).
// focus : {lat,lng} pour centrer sur une fiche precise (bouton "Voir sur la carte").
export function MapScreen({ ads = [], onBack, lang = "fr", user = {}, navigate = () => {}, mode = "all", focus = null }) {
  const t = MAP_I18N[lang] || MAP_I18N.fr;
  const iRef = useRef(null);
  const focusDone = useRef(false);
  const [pros, setPros] = useState([]);
  // Commutateur Tout / Annonces / Shop / Pro / HorseRoad. Il n'apparait que sur la
  // carte "exploration" : quand on arrive depuis une fiche precise (focus), on n'a
  // recu QUE cet element, les autres onglets seraient vides.
  const [kind, setKind] = useState(mode === "pros" ? "pros" : "all");
  const showSwitch = !focus;
  // Second niveau : sous-categorie. Le vocabulaire change selon ce qu'on regarde —
  // familles de metiers pour l'annuaire, categories d'annonce, rayons du shop.
  const [sub, setSub] = useState(null);
  useEffect(() => { setSub(null); }, [kind]);
  const [q, setQ] = useState("");
  const [aiIds, setAiIds] = useState(null); // Set de clés préfixées (ad_/pro_) ou null
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMsg, setAiMsg] = useState("");
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => { let on = true; getPros().then(d => { if (on) setPros(d || []); }); return () => { on = false; }; }, []);
  // Découverte invités : le visiteur non connecté ouvre la carte plein écran.
  useEffect(() => { if (!user?.id) trackGuestEvent("guest_map", lang); }, []);

  // Marqueurs combinés (annonces + pros), clés préfixées pour router au clic.
  const allMarkers = useMemo(() => {
    const out = [];
    const isGear = a => a.cat === "gear" || a.category === "gear";
    const adsForKind = (kind === "pros") ? []
      : (kind === "shop") ? (ads || []).filter(isGear)
      : (kind === "annonces") ? (ads || []).filter(a => !isGear(a))
      : (ads || []);
    adsForKind.forEach(a => {
      if (a.hideFromMap) return;
      let coords = null; try { coords = getAdCoords(a); } catch(e) {}
      if (!coords) return;
      out.push({ key:"ad_"+a.id, id:"ad_"+a.id, title:a.title, cat:a.cat, _sub:a.subcat||"", loc:a.loc||[a.city,a.region].filter(Boolean).join(", "), price:a.price||"", coords, photo:a.photos?.[0]||"", _text:[a.title,a.desc,a.city,a.region,a.loc].filter(Boolean).join(" ").toLowerCase() });
    });
    ((kind === "annonces" || kind === "shop") ? [] : (pros || [])).forEach(p => {
      let coords = null; try { coords = getAdCoords(p); } catch(e) {}
      if (!coords) return;
      out.push({ key:"pro_"+p.id, id:"pro_"+p.id, title:p.name, cat:MAP_FAM[p.family]||"other", _fam:p.family||"", loc:[p.city,p.region].filter(Boolean).join(", "), price:"", coords, photo:p.logo_url||p.cover_url||(p.photos&&p.photos[0])||"", radiusKm:(p.details&&p.details.radius_km)||0, _text:[p.name,p.description,p.city,p.region,(p.metiers||[]).join(" ")].filter(Boolean).join(" ").toLowerCase() });
    });
    return out;
  }, [ads, pros, kind]);

  // Sous-categories reellement presentes sur la carte. On les deduit des marqueurs
  // plutot que d'afficher une liste figee : pas de filtre qui ne renvoie rien.
  const subOptions = useMemo(() => {
    if (kind === "pros") {
      const here = new Set(allMarkers.map(m => m._fam).filter(Boolean));
      return (PRO_FAMILIES || []).filter(f => here.has(f.id))
        .map(f => ({ id:f.id, label:(f.i18n && (f.i18n[lang] || f.i18n.fr)) || f.id }));
    }
    if (kind === "shop") {
      const here = new Set(allMarkers.map(m => m._sub || "autres"));
      return (MSUBCATS || []).filter(c => c.id !== "all" && here.has(c.id))
        .map(c => ({ id:c.id, label:(c.i18n && (c.i18n[lang] || c.i18n.fr)) || c.label }));
    }
    if (kind === "annonces") {
      const here = new Set(allMarkers.map(m => m.cat).filter(Boolean));
      return (PUBLISH_CATEGORIES || []).filter(c => here.has(c.id)).map(c => {
        const full = (c.i18n && (c.i18n[lang] || c.i18n.fr)) || c.id;
        // Les libelles de publication sont longs ("Chevaux (vente / don / a placer)") :
        // sur une pastille on ne garde que la tete.
        return { id:c.id, label: full.split("(")[0].split("/")[0].trim() };
      });
    }
    return [];
  }, [allMarkers, kind, lang]);

  // Filtrage : IA (clés retournées) sinon recherche texte instantanée sinon tout.
  const shown = useMemo(() => {
    let list = allMarkers;
    if (sub) list = list.filter(m => kind === "pros" ? m._fam === sub : kind === "shop" ? (m._sub || "autres") === sub : m.cat === sub);
    if (aiIds) return list.filter(m => aiIds.has(m.key));
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter(m => m._text.includes(s));
  }, [allMarkers, aiIds, q, sub, kind]);

  const runAi = async () => {
    const query = q.trim();
    setAiMsg("");
    if (!query) return;
    if (!user?.id) { setAiMsg(t.login); return; }
    setAiLoading(true);
    try {
      const pool = allMarkers.slice(0, 160).map(m => ({ id:m.key, cat:m.cat, title:m.title, loc:m.loc }));
      const { data, error } = await supabase.functions.invoke("ai-search", { body: { query, ads:pool, lang } });
      if (error || !data || data.error || !Array.isArray(data.ids)) { setAiMsg(t.error); }
      else { const set = new Set(data.ids); setAiIds(set); if (data.ids.length === 0) setAiMsg(t.none); }
    } catch(e) { setAiMsg(t.error); }
    setAiLoading(false);
  };
  const clearAi = () => { setAiIds(null); setAiMsg(""); };

  // Repli 9s si le CDN Leaflet ne répond pas.
  useEffect(() => { if (ready) return; const to = setTimeout(() => setFailed(true), 9000); return () => clearTimeout(to); }, [ready, reloadKey]);
  const retry = () => { setFailed(false); setReady(false); setReloadKey(k => k + 1); };

  // Injecter les marqueurs filtrés dans la carte.
  useEffect(() => {
    if (!ready || !iRef.current?.contentWindow) return;
    const w = iRef.current.contentWindow;
    // Centrage sur une fiche precise (bouton "Voir sur la carte") : on coupe d'abord
    // le recadrage auto de la carte. Sinon son fitBounds anime le zoom et Leaflet
    // ignore purement et simplement le setView qui suit — la carte restait sur la
    // vue d'ensemble, comme si le bouton ne faisait rien.
    // Le centrage ne vaut que pour l'arrivee : une fois consomme, la carte reprend
    // son recadrage normal, sinon chaque recherche ramenerait sur la fiche de depart.
    const wantFocus = !!(focus && focus.lat != null && focus.lng != null) && !focusDone.current;
    try { w.setMarkersAutoFit(!wantFocus); } catch(e) {}
    try { w.updateMarkers(JSON.stringify(shown)); } catch(e) {}
    if (wantFocus) {
      try { w.setMapView(focus.lat, focus.lng, 13); } catch(e) {}
      // Puis on ouvre la bulle de la fiche : on arrive sur SA fiche, pas sur un
      // point anonyme au milieu des autres.
      if (focus.id) { try { w.openMarkerPopup(focus.id); } catch(e) {} }
      // On n'arme le "consomme" qu'une fois les marqueurs reellement affiches :
      // le premier passage arrive souvent avant le chargement des fiches.
      if (shown.length > 0) focusDone.current = true;
    }
  }, [shown, ready, focus]);

  // Réception : carte prête + clic marqueur -> ouvrir la bonne fiche.
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === "horseconnex_map_ready") setReady(true);
      if (e.data?.type === "horseconnex_ad_click" && e.data.adId) {
        const id = String(e.data.adId);
        if (id.startsWith("pro_")) { const p = (pros||[]).find(x => x.id === id.slice(4)); if (p) navigate("proDetail", { pro:p }); }
        else if (id.startsWith("ad_")) { const a = (ads||[]).find(x => x.id === id.slice(3)); if (a) navigate("adDetail", { ad:a }); }
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [ads, pros, navigate]);

  const aiActive = aiIds !== null;
  return (
    <div style={{ position:"fixed", inset:0, zIndex:50, background:"#0d0d1a", display:"flex", flexDirection:"column" }}>
      {/* Barre haute : retour + recherche IA */}
      <div style={{ position:"relative", zIndex:2, padding:"calc(env(safe-area-inset-top,0px) + 10px) 12px 10px", display:"flex", alignItems:"center", gap:9, background:"rgba(13,13,26,.9)", backdropFilter:"blur(14px)", borderBottom:"1px solid rgba(255,255,255,.08)" }}>
        <button onClick={onBack} aria-label={t.title} style={{ width:40, height:40, flexShrink:0, borderRadius:11, border:"1px solid rgba(255,255,255,.14)", background:"rgba(255,255,255,.06)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
          <CIcon name="back" s={20} c="#fff"/>
        </button>
        <div style={{ position:"relative", flex:1 }}>
          <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", display:"flex" }}><CIcon name="search" s={15} c="rgba(255,255,255,.5)"/></span>
          <input value={q} onChange={e=>{ setQ(e.target.value); if(aiIds) clearAi(); }} onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); runAi(); } }} placeholder={mode==="pros" ? t.searchPros : t.search}
            style={{ width:"100%", boxSizing:"border-box", padding:"11px 46px 11px 34px", background:"rgba(255,255,255,.06)", border:"1px solid rgba(255,255,255,.15)", borderRadius:12, fontSize:13, color:"#fff", outline:"none" }}/>
          {q && <span onClick={()=>{ setQ(""); clearAi(); }} style={{ position:"absolute", right:44, top:"50%", transform:"translateY(-50%)", cursor:"pointer", display:"flex" }}><CIcon name="close" s={14} c="rgba(255,255,255,.6)"/></span>}
          <button onClick={runAi} disabled={aiLoading} title={t.ai} style={{ position:"absolute", right:5, top:"50%", transform:"translateY(-50%)", width:32, height:32, borderRadius:9, border:"none", background:"linear-gradient(135deg,#8B5CF6,#C8001A)", display:"flex", alignItems:"center", justifyContent:"center", cursor:aiLoading?"default":"pointer", boxShadow:"0 3px 10px rgba(124,58,237,.55)" }}>
            <span style={{ fontSize:15, lineHeight:1 }}>{aiLoading?"⏳":"✨"}</span>
          </button>
        </div>
      </div>

      {/* Commutateur de contenu. HorseRoad n'est pas un filtre : ses trajets sont des
          itineraires (une ligne depart -> arrivee), pas des points. Il passe donc la
          main a la carte des itineraires, qui sait les dessiner. */}
      {showSwitch && (
        <div style={{ position:"relative", zIndex:2, display:"flex", gap:6, padding:"8px 12px", background:"rgba(13,13,26,.85)", overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
          {[["all",t.kAll],["annonces",t.kAds],["shop",t.kShop],["pros",t.kPros],["horseroad",t.kRoad]].map(([k,label]) => {
            const on = k === kind;
            return (
              <button key={k}
                onClick={() => k === "horseroad" ? navigate("horseroadMap", {}) : setKind(k)}
                style={{
                  flexShrink:0, padding:"7px 13px", borderRadius:20, cursor:"pointer",
                  border: on ? "1px solid transparent" : "1px solid rgba(255,255,255,.18)",
                  background: on ? "var(--app-cta-bg)" : "rgba(255,255,255,.06)",
                  color:"#fff", fontSize:12, fontWeight:800, fontFamily:"var(--fh)", whiteSpace:"nowrap",
                }}>
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* Sous-categories : n'apparait que si le niveau du dessus a ete choisi
          (sur "Tout", annonces et fiches Pro n'ont pas le meme vocabulaire) et
          qu'il y a au moins deux choix — un seul filtre ne filtre rien. */}
      {showSwitch && subOptions.length > 1 && (
        <div style={{ position:"relative", zIndex:2, display:"flex", gap:6, padding:"0 12px 8px", background:"rgba(13,13,26,.85)", overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
          {[{ id:null, label:t.kAll }, ...subOptions].map(o => {
            const on = o.id === sub;
            return (
              <button key={o.id || "_all"} onClick={() => setSub(o.id)}
                style={{
                  flexShrink:0, padding:"5px 11px", borderRadius:16, cursor:"pointer",
                  border: on ? "1px solid var(--app-cta-bg)" : "1px solid rgba(255,255,255,.14)",
                  background: on ? "rgba(200,0,26,.28)" : "transparent",
                  color: on ? "#fff" : "rgba(255,255,255,.72)",
                  fontSize:11.5, fontWeight:700, whiteSpace:"nowrap",
                }}>
                {o.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Bandeau état recherche */}
      {(aiActive || aiLoading || aiMsg) && (
        <div style={{ position:"relative", zIndex:2, padding:"7px 12px", background:"rgba(13,13,26,.8)", display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
          <span style={{ fontSize:12, fontWeight:700, color:"#EADBFF", fontFamily:"var(--fh)", display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ fontSize:14 }}>✨</span>{aiLoading ? t.loading : aiActive ? `${t.results} (${shown.length})` : (aiMsg || "")}
          </span>
          {aiActive && <span onClick={clearAi} style={{ fontSize:11.5, fontWeight:700, color:"rgba(255,255,255,.7)", cursor:"pointer", whiteSpace:"nowrap" }}>✕ {t.clear}</span>}
        </div>
      )}
      {aiMsg && !aiActive && !aiLoading && <div style={{ position:"relative", zIndex:2, fontSize:11.5, color:"var(--hc-danger)", textAlign:"center", padding:"4px 12px" }}>{aiMsg}</div>}

      {/* Carte plein écran */}
      <div style={{ flex:1, position:"relative" }}>
        <iframe key={reloadKey} ref={iRef} srcDoc={MAP_HTML} style={{ width:"100%", height:"100%", border:"none", display:"block" }} title="HorseConnex Map" allow="geolocation" sandbox="allow-scripts allow-same-origin"/>
        {!ready && !failed && (
          <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:12, background:"rgba(13,13,26,.82)", zIndex:5 }}>
            <motion.div animate={{ rotate:360 }} transition={{ duration:0.8, repeat:Infinity, ease:"linear" }} style={{ width:32, height:32, border:"3px solid rgba(255,255,255,.18)", borderTopColor:C.red, borderRadius:"50%" }}/>
            <div style={{ color:"rgba(255,255,255,.75)", fontSize:12, fontWeight:600 }}>{t.mapLoading}</div>
          </div>
        )}
        {failed && !ready && (
          <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:12, padding:24, textAlign:"center", background:"rgba(13,13,26,.93)", zIndex:6 }}>
            <div style={{ fontSize:34 }}>🗺️</div>
            <div style={{ color:"#fff", fontSize:14, fontWeight:700, fontFamily:"var(--fh)" }}>{t.mapDown}</div>
            <div style={{ color:"rgba(255,255,255,.6)", fontSize:12, maxWidth:240 }}>{t.mapDownSub}</div>
            <button onClick={retry} style={{ background:"var(--app-cta-bg)", color:"#fff", border:"none", borderRadius:10, padding:"9px 22px", fontSize:13, fontWeight:700, fontFamily:"var(--fh)", cursor:"pointer" }}>↻ {t.retry}</button>
          </div>
        )}
        <div style={{ position:"absolute", bottom:14, left:"50%", transform:"translateX(-50%)", background:"rgba(13,13,26,.85)", color:"rgba(255,255,255,.8)", fontSize:11, fontWeight:600, padding:"6px 14px", borderRadius:20, zIndex:5, whiteSpace:"nowrap" }}>
          <span style={{ color:"#fff", fontWeight:800, fontFamily:"var(--fh)" }}>{shown.length}</span> {t.count}
        </div>
      </div>
    </div>
  );
}

export function TabMessages({onChat,lang="fr"}) {
  const t = useLang(lang);
  const [conversations, setConversations] = useState([]);
  const [loadingConvos, setLoadingConvos] = useState(true);

  useEffect(() => {
    loadConversations();
    // Écouter les nouveaux messages en temps réel
    const channel = supabase.channel("messages-changes")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" }, () => {
        loadConversations();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const deleteConversation = async (convoId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(t.deleteConvConfirm)) return;
    try {
      // 1. Supprimer tous les messages de la conversation
      await supabase.from("messages").delete().eq("conversation_id", convoId);
      // 2. Supprimer les notifications liees
      await supabase.from("notifications").delete().eq("link", "chat:" + convoId);
      // 3. Supprimer la conversation elle-meme
      const { error } = await supabase.from("conversations").delete().eq("id", convoId);
      if (error) {
        alert("Erreur lors de la suppression");
        console.error(error);
      } else {
        setConversations(prev => prev.filter(c => c.id !== convoId));
      }
    } catch(err) {
      console.error("Delete conv error:", err);
      alert("Erreur lors de la suppression");
    }
  };
  
  const loadConversations = async () => {
    try {
      const authRes = await supabase.auth.getUser();
      const authUser = authRes.data.user;
      if (!authUser) { setLoadingConvos(false); return; }

      const { data, error } = await supabase
        .from("conversations")
        .select("*")
        .or("user1_id.eq." + authUser.id + ",user2_id.eq." + authUser.id)
        .order("last_message_at", { ascending: false });

      if (error) { console.error("Erreur chargement conversations:", error); setLoadingConvos(false); return; }
      const convos = data || [];

      // Blocage bidirectionnel : masque les conversations dans les deux sens
      // (ceux que j'ai bloqués ET ceux qui m'ont bloqué).
      const blockedSet = await getBlockedIds();

      // FK conversations.user1_id/user2_id pointent vers auth.users (sans prenom/nom) et ad_id
      // n'a pas de FK : on charge les profils et titres separement plutot que via embed PostgREST.
      const otherIds = [...new Set(convos.map(c => c.user1_id === authUser.id ? c.user2_id : c.user1_id).filter(Boolean))];
      const adIds = [...new Set(convos.map(c => c.ad_id).filter(Boolean))];

      const profilesById = {};
      if (otherIds.length > 0) {
        const { data: profs } = await supabase
          .from("profiles_public")
          .select("id,prenom,nom,avatar_url")
          .in("id", otherIds);
        (profs || []).forEach(p => { profilesById[p.id] = p; });
      }

      const adTitleById = {};
      if (adIds.length > 0) {
        const { data: adsData } = await supabase
          .from("ads")
          .select("id,title")
          .in("id", adIds);
        (adsData || []).forEach(a => { adTitleById[a.id] = a.title; });
      }

      const formatted = convos.map(c => {
        const otherId = c.user1_id === authUser.id ? c.user2_id : c.user1_id;
        const other = profilesById[otherId];
        const otherName = other ? ((other.prenom||"") + " " + (other.nom||"")).trim() : t.userFallback;
        return {
          id: c.id,
          user1_id: c.user1_id,
          user2_id: c.user2_id,
          ad_id: c.ad_id,
          n: otherName || t.userFallback,
          av: ((otherName[0]) || "?").toUpperCase(),
          col: "#" + Math.floor(parseInt(c.id.replace(/[^0-9a-f]/g,"").slice(0,6) || "888888", 16) % 0xffffff).toString(16).padStart(6, "0"),
          sub: (c.ad_id && adTitleById[c.ad_id]) || "",
          time: c.last_message_at ? new Date(c.last_message_at).toLocaleDateString("fr-FR", {day:"2-digit", month:"2-digit"}) : "",
          lastMsg: c.last_message || "",
          unread: 0,
          otherId: otherId,
          otherAvatar: other?.avatar_url,
          adId: c.ad_id,
        };
      });
      setConversations(formatted.filter(c => !blockedSet.has(c.otherId)));
    } catch(err) {
      console.error("Erreur chargement conversations:", err);
    }
    setLoadingConvos(false);
  };

  return (
    <div style={{padding:"14px 16px"}}>
      <div style={{
        fontFamily:"var(--fh)", fontSize:20, fontWeight:900,
        color:"var(--hc-text)", marginBottom:14, letterSpacing:.3,
      }}>{t.messages}</div>
      {loadingConvos && (
        <div style={{textAlign:"center", padding:"40px 20px", color:"var(--hc-text-muted)", fontSize:13}}>{t.loading}</div>
      )}
      {!loadingConvos && conversations.length===0 && (
        <div style={{textAlign:"center", padding:"60px 20px", color:"var(--hc-text-muted)"}}>
          <div style={{fontSize:42, marginBottom:14}}>💬</div>
          <div style={{fontSize:16, fontWeight:700, color:"var(--hc-text)", marginBottom:6, fontFamily:"var(--fh)"}}>{t.noMessages}</div>
          <div style={{fontSize:13, lineHeight:1.6}}>{t.messagesHere}</div>
        </div>
      )}
      {conversations.map(c=>(
        <motion.div
          key={c.id}
          whileTap={{ scale: 0.98 }}
          onClick={()=>onChat(c)}
          style={{
            background:"var(--hc-surface)",
            backdropFilter:"blur(14px)",
            border:"1px solid var(--hc-border)",
            borderRadius:14,
            padding:"11px 13px", marginBottom:8,
            boxShadow:"0 8px 24px rgba(0,0,0,.14), 0 2px 6px rgba(0,0,0,.10)",
            display:"flex", alignItems:"center", gap:11,
            cursor:"pointer",
          }}
        >
          <div style={{position:"relative"}}>
            {c.otherAvatar ? (
              <img src={c.otherAvatar} alt={c.n} style={{width:46, height:46, borderRadius:"50%", objectFit:"cover", flexShrink:0, border:"2px solid rgba(255,255,255,.1)"}}/>
            ) : (
              <div style={{
                width:46, height:46, borderRadius:"50%",
                background: c.col,
                display:"flex", alignItems:"center", justifyContent:"center",
                color:"#fff", fontWeight:800, fontSize:16, fontFamily:"var(--fh)",
                flexShrink:0,
                border:"2px solid rgba(255,255,255,.15)",
              }}>{c.av}</div>
            )}
            {c.unread > 0 && (
              <div style={{
                position:"absolute", top:-3, right:-3,
                minWidth:18, height:18, borderRadius:9,
                background: C.red,
                border:"2px solid var(--hc-bg)",
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:9, color:"#fff", fontWeight:800,
                padding:"0 4px",
              }}>{c.unread}</div>
            )}
          </div>
          <div style={{flex:1, minWidth:0}}>
            <div style={{display:"flex", justifyContent:"space-between", marginBottom:2}}>
              <span style={{fontSize:14, fontWeight:700, color:"var(--hc-text)", fontFamily:"var(--fh)"}}>{c.n}</span>
              <span style={{fontSize:10.5, color:"var(--hc-text-faint)"}}>{c.time}</span>
            </div>
            {c.sub && <div style={{fontSize:11, color:"var(--hc-accent)", marginBottom:2, fontWeight:600}}>{c.sub}</div>}
            <div style={{fontSize:12, color:"var(--hc-text-muted)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{c.lastMsg}</div>
          </div>
          <motion.button
            whileTap={{ scale: 0.85 }}
            onClick={(e)=>deleteConversation(c.id, e)}
            style={{
              background:"transparent", border:"none",
              cursor:"pointer",
              color:"var(--hc-text-faint)",
              padding:8, fontSize:18, flexShrink:0,
            }}
            aria-label={t.delete}
          >🗑️</motion.button>
        </motion.div>
      ))}
    </div>
  );
}

export function TabProfil({navigate,showToast,userCats=[],user={},setUser,lang="fr"}) {
  const t = useLang(lang);
  const [theme, setTheme] = useState(getCurrentTheme());
  const switchTheme = (id) => setTheme(applyTheme(id));
  const isPro = (user?.cats || []).some(c => PRO_METIERS[c]);
  const menuItems=[
    {icon:"edit",    label:t.editProfile,            screen:"profileEdit"},
    ...(isPro ? [{icon:"crown", label:({fr:"Ma fiche Pro",en:"My Pro listing",de:"Mein Profi-Eintrag",es:"Mi ficha Pro",it:"La mia scheda Pro",pt:"A minha ficha Pro",nl:"Mijn Pro-fiche"}[lang]||"Ma fiche Pro"), action: async()=>{ try { const mine = await getMyPros(); navigate("proFicheEdit", { pro: (mine && mine[0]) || null }); } catch(e){ navigate("proFicheEdit", { pro:null }); } }}] : []),
    {icon:"list",    label:t.myAds,                  screen:"myAds"},
    {icon:"heart",   label:t.myFavorites,            screen:"favorites"},
    {icon:"star",    label:t.myReviews,              action:()=>showToast({type:"success",text:t.myReviewsPrefix+" 4.8/5 ⭐"})},
    {icon:"crown",   label: user?.is_pro ? t.proYourBenefits + " " + (user?.pro_plan === "pro_max" ? "PRO MAX 👑" : "PRO ⭐") : t.goPro,        screen:"premium", highlight:true},
    {icon:"star",    label:t.myReputation,           screen:"myReputation", highlight:false},
    {icon:"bell",    label:t.notifications,          screen:"notifications"},
    {icon:"globe",   label:t.language,               screen:"langSettings"},
    {icon:"shield",  label:t.security,               screen:"security"},
    {icon:"help",    label:t.help,                   screen:"help"},
    {icon:"horses",  label:t.dTabAbout,              screen:"about"},
    {icon:"mail",    label:t.contactSupport,         screen:"contactSAV"},
  ];
  return (
    <div style={{padding:"14px 16px 20px"}}>
      {/* Header carte profil — gradient rouge + shine + glow */}
      <motion.div
        whileTap={{ scale: 0.99 }}
        style={{
          background:"var(--app-cta-bg)",
          borderRadius:16,
          padding:"18px 16px",
          display:"flex", alignItems:"center", gap:13,
          marginBottom:14,
          boxShadow:"0 10px 28px rgba(200,0,26,.4), inset 0 1px 0 rgba(255,255,255,.18)",
          position:"relative", overflow:"hidden",
        }}
      >
        <motion.span
          aria-hidden
          initial={{ x: "-130%" }}
          animate={{ x: "230%" }}
          transition={{ duration: 1.4, repeat: Infinity, repeatDelay: 6, ease: "easeInOut" }}
          style={{
            position:"absolute", top:0, left:0, bottom:0, width:"55%",
            background:"linear-gradient(110deg, transparent 0%, rgba(255,255,255,.2) 50%, transparent 100%)",
            pointerEvents:"none",
          }}
        />
        <div style={{
          width:60, height:60, borderRadius:"50%",
          background: user?.avatar
            ? `url(${user.avatar}) center/cover, rgba(255,255,255,.2)`
            : "rgba(255,255,255,.2)",
          backgroundSize: user?.avatar ? "cover" : undefined,
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:26,
          border:"2px solid rgba(255,255,255,.3)",
          flexShrink:0, position:"relative", zIndex:1,
          overflow:"hidden",
        }}>{user?.avatar ? null : "👤"}</div>
        <div style={{flex:1, position:"relative", zIndex:1, minWidth:0}}>
          <div style={{fontFamily:"var(--fh)", fontSize:19, fontWeight:900, color:"#fff", letterSpacing:.3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
            {user?.prenom||"Mon"} {user?.nom||"Profil"}
          </div>
          <div style={{fontSize:11, color:"rgba(255,255,255,.85)", marginTop:2}}>
            {user?.verified ? "✓ Membre vérifié · " : ""}{user?.cats?.[0] || ""}
          </div>
          {user?.isFounder && (
            <div style={{
              display:"inline-flex", alignItems:"center", gap:4, marginTop:6,
              padding:"3px 10px",
              background:"linear-gradient(135deg, #FFD700, #FFA500)",
              borderRadius:12,
              fontSize:10, fontWeight:800, color:"#1A1A2E",
              letterSpacing:.5, textTransform:"uppercase",
            }}>⭐ Founder Member</div>
          )}
        </div>
        <motion.button
          whileTap={{ scale: 0.93 }}
          onClick={()=>navigate("profileEdit")}
          style={{
            background:"rgba(255,255,255,.18)",
            border:"1px solid rgba(255,255,255,.25)",
            borderRadius:10, padding:"7px 12px",
            color:"#fff",
            fontSize:11, fontFamily:"var(--fh)", fontWeight:700,
            cursor:"pointer", flexShrink:0,
            position:"relative", zIndex:1,
          }}
        >{t.editBtn}</motion.button>
      </motion.div>

      {/* Lien Réputation — accent doré */}
      <motion.div
        whileTap={{ scale: 0.98 }}
        onClick={()=>navigate("myReputation")}
        style={{
          background:"rgba(255,200,80,.08)",
          backdropFilter:"blur(12px)",
          borderRadius:12,
          padding:"12px 14px",
          border:"1px solid rgba(255,200,80,.3)",
          cursor:"pointer",
          display:"flex", alignItems:"center", justifyContent:"space-between",
          marginBottom:14,
        }}
      >
        <div style={{display:"flex", alignItems:"center", gap:11}}>
          <div style={{fontSize:24, filter:"drop-shadow(0 0 8px rgba(255,200,80,.6))"}}>⭐</div>
          <div>
            <div style={{fontSize:13.5, fontWeight:800, color:"var(--hc-text)", fontFamily:"var(--fh)"}}>{t.myReputation}</div>
            <div style={{fontSize:10.5, color:"var(--hc-text-muted)"}}>{t.seeBadgesScore}</div>
          </div>
        </div>
        <div style={{fontSize:18, color:"#E08A00"}}>→</div>
      </motion.div>

      {/* Sélecteur de thème — Apparence */}
      <div style={{
        background:"var(--hc-surface)",
        backdropFilter:"blur(12px)",
        borderRadius:14,
        padding:"14px 16px",
        border:"1px solid var(--hc-border)",
        marginBottom:14,
      }}>
        <div style={{
          fontSize:10.5, fontWeight:700,
          color:"var(--hc-text-muted)",
          letterSpacing:1.4, textTransform:"uppercase",
          marginBottom:10,
        }}>🎨 {t.themeSection || "Apparence"}</div>
        <div style={{display:"flex", gap:8}}>
          {Object.values(THEMES).filter(th => th.id === "night" || th.id === "light").map(th => {
            const active = theme === th.id;
            return (
              <motion.button
                key={th.id}
                whileTap={{ scale: 0.96 }}
                onClick={() => switchTheme(th.id)}
                style={{
                  flex:1,
                  padding:"12px 8px",
                  background: active
                    ? "var(--app-cta-bg)"
                    : "var(--hc-surface-2)",
                  border: active
                    ? "2px solid rgba(255,255,255,.25)"
                    : "2px solid var(--hc-border)",
                  borderRadius:12,
                  cursor:"pointer",
                  display:"flex", flexDirection:"column", alignItems:"center", gap:5,
                  boxShadow: active ? "0 6px 18px rgba(200,0,26,.4)" : "none",
                  transition:"all .15s",
                }}
              >
                <span style={{fontSize:22, filter: active ? "drop-shadow(0 0 6px rgba(255,255,255,.5))" : "none"}}>{th.emoji}</span>
                <span style={{
                  fontSize:11, fontWeight:800,
                  color: active ? "#fff" : "var(--hc-text)",
                  fontFamily:"var(--fh)", letterSpacing:.2,
                  textAlign:"center", lineHeight:1.15,
                }}>{th.label}</span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Menu */}
      {menuItems.map(({icon,label,screen,action,highlight})=>(
        <motion.div
          key={label}
          whileTap={{ scale: 0.98 }}
          onClick={screen?()=>navigate(screen):action}
          style={{
            background: highlight
              ? "linear-gradient(135deg, rgba(255,180,80,.12) 0%, rgba(255,200,120,.06) 100%)"
              : "var(--hc-surface)",
            backdropFilter:"blur(12px)",
            borderRadius:12,
            padding:"12px 13px",
            marginBottom:6,
            display:"flex", alignItems:"center", justifyContent:"space-between",
            cursor:"pointer",
            border: `1px solid ${highlight ? "rgba(255,180,80,.3)" : "var(--hc-border)"}`,
          }}
        >
          <div style={{display:"flex", alignItems:"center", gap:11}}>
            <div style={{
              width:34, height:34, borderRadius:10,
              background: highlight ? "rgba(255,180,80,.18)" : "var(--hc-accent-soft)",
              border: `1px solid ${highlight ? "rgba(255,180,80,.3)" : "var(--hc-accent-soft)"}`,
              display:"flex", alignItems:"center", justifyContent:"center",
              flexShrink:0,
            }}>
              <CIcon name={icon} s={16} c={highlight ? "#E08A00" : "var(--hc-accent)"}/>
            </div>
            <span style={{
              fontSize:13.5,
              fontWeight: highlight ? 800 : 600,
              color: highlight ? "#E08A00" : "var(--hc-text)",
              fontFamily:"var(--fh)",
            }}>{label}</span>
          </div>
          <CIcon name="chevron" s={16} c={highlight ? "#E08A00" : "var(--hc-text-faint)"}/>
        </motion.div>
      ))}

      {/* Bouton admin — gradient rouge intense */}
      {userCats.includes("Admin") && (
        <motion.div
          whileTap={{ scale: 0.98 }}
          onClick={()=>navigate("admin")}
          style={{
            background:"var(--app-cta-bg)",
            borderRadius:12,
            padding:"14px 13px",
            marginBottom:6, marginTop:10,
            display:"flex", alignItems:"center", justifyContent:"space-between",
            cursor:"pointer",
            boxShadow:"0 8px 22px rgba(200,0,26,.5), inset 0 1px 0 rgba(255,255,255,.18)",
            position:"relative", overflow:"hidden",
          }}
        >
          <motion.span
            aria-hidden
            initial={{ x: "-130%" }}
            animate={{ x: "230%" }}
            transition={{ duration: 1.2, repeat: Infinity, repeatDelay: 5, ease: "easeInOut" }}
            style={{
              position:"absolute", top:0, left:0, bottom:0, width:"55%",
              background:"linear-gradient(110deg, transparent 0%, rgba(255,255,255,.3) 50%, transparent 100%)",
              pointerEvents:"none",
            }}
          />
          <div style={{display:"flex", alignItems:"center", gap:11, position:"relative", zIndex:1}}>
            <div style={{
              width:34, height:34, borderRadius:10,
              background:"rgba(0,0,0,.25)",
              display:"flex", alignItems:"center", justifyContent:"center",
              flexShrink:0,
              border:"1px solid rgba(255,255,255,.2)",
            }}>
              <CIcon name="shield" s={18} c="#fff"/>
            </div>
            <span style={{fontSize:13.5, fontWeight:900, color:"#fff", letterSpacing:.4, fontFamily:"var(--fh)"}}>⚡ Panneau Administrateur</span>
          </div>
          <CIcon name="chevron" s={16} c="#fff"/>
        </motion.div>
      )}

      {/* Bouton logout */}
      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={()=>navigate("logout")}
        style={{
          width:"100%",
          background:"transparent",
          border:`1.5px solid var(--hc-accent)`,
          color:"var(--hc-accent)",
          borderRadius:12,
          padding:"12px 16px",
          marginTop:10,
          fontFamily:"var(--fh)", fontSize:14, fontWeight:800, letterSpacing:.3,
          cursor:"pointer",
          display:"flex", alignItems:"center", justifyContent:"center", gap:8,
        }}
      >
        <CIcon name="logout" s={16} c="var(--hc-accent)"/>{t.logout}
      </motion.button>
    </div>
  );
}

// Hub d'accueil (New Modern App) : tuiles vers Annonces / Shop / Pros / Carte.
function HubTiles({ lang, onSelect }) {
  const L = {
    annonces:{fr:"Annonces",en:"Listings",de:"Anzeigen",es:"Anuncios",it:"Annunci",pt:"Anúncios",nl:"Advertenties"},
    shop:{fr:"Shop",en:"Shop",de:"Shop",es:"Tienda",it:"Shop",pt:"Loja",nl:"Shop"},
    pros:{fr:"Pros",en:"Pros",de:"Profis",es:"Pros",it:"Pro",pt:"Profis",nl:"Pro's"},
    carnet:{fr:"Carnet",en:"Logbook",de:"Heft",es:"Cuaderno",it:"Libretto",pt:"Caderno",nl:"Logboek"},
    carte:{fr:"Carte",en:"Map",de:"Karte",es:"Mapa",it:"Mappa",pt:"Mapa",nl:"Kaart"},
    trails:{fr:"Rando",en:"Trails",de:"Wege",es:"Rutas",it:"Percorsi",pt:"Trilhos",nl:"Trektocht"},
  };
  const tiles=[{id:"annonces",icon:"list"},{id:"shop",icon:"shop"},{id:"pros",icon:"crown"},{id:"carnet",icon:"horses"},{id:"trails",icon:"pin"}];
  return (
    <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8,padding:"14px 16px 2px"}}>
      {tiles.map(tl=>{
        // L'annuaire Pro est la brique qu'on pousse en ce moment : sa tuile est un
        // peu plus grande et respire (halo + etoile). Le scintillement est un halo
        // rouge de marque, lisible sur fond clair comme sur fond sombre — un reflet
        // blanc disparaitrait en theme clair.
        const star = tl.id === "pros";
        return (
        <motion.button key={tl.id} whileTap={{scale:0.95}} onClick={()=>onSelect(tl.id)}
          animate={star ? { boxShadow:["0 0 0 0 rgba(200,0,26,0)","0 0 14px 2px rgba(200,0,26,.45)","0 0 0 0 rgba(200,0,26,0)"] } : undefined}
          transition={star ? { duration:2.4, repeat:Infinity, ease:"easeInOut" } : undefined}
          style={{position:"relative",background:"var(--hc-surface-2)",border:star?"1.5px solid var(--hc-accent)":"1px solid var(--hc-border)",borderRadius:16,padding:star?"17px 6px":"14px 6px",display:"flex",flexDirection:"column",alignItems:"center",gap:7,cursor:"pointer"}}>
          {star && (
            <motion.span aria-hidden animate={{opacity:[0.35,1,0.35],scale:[0.9,1.1,0.9]}} transition={{duration:2.4,repeat:Infinity,ease:"easeInOut"}}
              style={{position:"absolute",top:4,right:5,fontSize:11,lineHeight:1,pointerEvents:"none"}}>✨</motion.span>
          )}
          <CIcon name={tl.icon} s={star?27:22} c="var(--hc-accent)"/>
          <span style={{fontSize:star?12.5:11.5,fontWeight:star?900:700,color:"var(--hc-text)",fontFamily:"var(--fh)"}}>{L[tl.id][lang]||L[tl.id].fr}</span>
        </motion.button>
      );})}
    </div>
  );
}

export function Dashboard({navigate,showToast,userCats=[],lang="fr",user={},setUser,initialTab,onTabChange}) {
  const t = useLang(lang);
  const [tab,setTab] = useState(initialTab || "home");
  const [search,setSearch]=useState("");
  const [filters,setFilters]=useState({});
  const [showSearchModal,setShowSearchModal]=useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  // Pastille sur l'icône de l'app (API Badging) reflétant les messages non lus.
  useEffect(() => { setBadge(unreadMessages || 0); }, [unreadMessages]);
  const [signupPrompt, setSignupPrompt] = useState(null);
  // Quick-toggle de thème (header) — cycle sur les 3 thèmes
  // (night → fire → hc-original → night), persisté via applyTheme.
  const [theme, setTheme] = useState(getCurrentTheme());
  const toggleTheme = () => {
    // New Modern App : bascule Nocturne <-> Clair (Feu retiré).
    const order = Object.keys(THEMES).filter(id => id === "night" || id === "light");
    const idx = order.indexOf(theme);
    const next = order[(idx + 1) % order.length] || order[0];
    setTheme(applyTheme(next));
    showToast?.({ type: "success", text: (THEMES[next]?.emoji || "") + " " + (THEMES[next]?.label || "") });
  };
  const themeMeta = THEMES[theme] || THEMES.night;

  // Mode invité (découverte sans compte) : pas d'id = pas connecté.
  // Toute action nécessitant un compte appelle requireAccount(msg) → popup d'invitation.
  const requireAccount = (msg) => { setSignupPrompt(msg); };
  const isGuest = !user?.id;

  // Synchroniser tab avec initialTab quand on revient de Chat
  useEffect(() => {
    if (initialTab) setTab(initialTab);
  }, [initialTab]);
  
  // Wrapper qui change le tab ET notifie App.jsx pour l'history
  const changeTab = (newTab) => {
    setTab(newTab);
    if (onTabChange) onTabChange(newTab);
  };

  // Bouton retour Android : depuis un onglet secondaire on revient à l'accueil
  // avant de pouvoir quitter l'app. Sans ça, un retour depuis « Pro » ou
  // « HorseRoad » fermait l'application d'un coup.
  useBackHandler(() => {
    if (tab !== "home") { changeTab("home"); return true; }
    return false;
  });
  
  // Charger le nombre de messages non lus + temps reel
  useEffect(() => {
    const loadUnread = async () => {
      try {
        const authRes = await supabase.auth.getUser();
        const authUser = authRes.data.user;
        if (!authUser) return;
        const { count } = await supabase
          .from("notifications")
          .select("*", { count: "exact", head: true })
          .eq("user_id", authUser.id)
          .eq("type", "message")
          .eq("read", false);
        setUnreadMessages(count || 0);
      } catch(e) { console.error("Erreur unread:", e); }
    };
    loadUnread();
    
    // Temps reel - ecouter les changements
    const channel = supabase.channel("dash-notifs")
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications" }, () => {
        loadUnread();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);
  const [realAds, setRealAds] = useState([]);

  // Charger les annonces depuis Supabase au démarrage
  useEffect(() => {
    loadAds();
  }, []);

  const loadAds = async () => {
    try {
      const { data, error } = await supabase
        .from("ads")
        .select("id, user_id, category, subcat, title, description, translations, price, location, latitude, longitude, lat, lng, photos, tags, tarifs, infras, boxes, prestations, age_range, status, views, boosted, created_at, location_type, coverage_regions, country, region, city, hide_from_map, horse_breed, horse_age, horse_sex, horse_discipline, horse_level, horse_height, contact_phone, contact_email, contact_message, video_url, videos, deleted_at")
        .eq("status", "active")
        .order("created_at", { ascending: false });

      // Filtre côté client pour deleted_at (au cas où la colonne n'existe pas)
      // + masquer les annonces des utilisateurs que j'ai bloqués
      const blockedIds = await getBlockedIds();
      const filteredData = data ? data.filter(a => !a.deleted_at && !blockedIds.has(a.user_id)) : [];

      // profiles n'est pas lisible en direct (RLS own+admin) : on lit les infos
      // vendeur publiques via la vue profiles_public (lookup groupe par user_id).
      const sellerIds = [...new Set(filteredData.map(a => a.user_id).filter(Boolean))];
      const sellerById = {};
      if (sellerIds.length > 0) {
        const { data: profs } = await supabase
          .from("profiles_public")
          .select("id, prenom, nom, avatar_url, verified, badge, display_name, use_display_name")
          .in("id", sellerIds);
        (profs || []).forEach(p => { sellerById[p.id] = p; });
      }

      if (filteredData && !error) {
        const formatted = filteredData.map(a => {
          // Construire l affichage de la localisation selon le type
          let displayLoc = a.location || "";
          if (a.location_type === "region" && a.region) {
            displayLoc = a.region;
          } else if (a.location_type === "coverage" && a.coverage_regions?.length > 0) {
            displayLoc = "🚐 " + a.coverage_regions.slice(0,3).join(", ") + (a.coverage_regions.length>3 ? " +" + (a.coverage_regions.length-3) : "");
          } else if (a.location_type === "precise" && a.city) {
            displayLoc = a.city + (a.region ? ", " + a.region : "");
          }
          return {
            id: a.id,
            cat: a.category,
            subcat: a.subcat || "",
            title: a.title,
            desc: a.description,
            translations: a.translations || null,
            price: a.price,
            loc: displayLoc,
            photos: a.photos || [],
            tags: a.tags || [],
            tarifs: a.tarifs || [],
            infras: a.infras || [],
            boxes: a.boxes || [],
            prestations: a.prestations || [],
            ageRange: a.age_range,
            locationType: a.location_type,
            country: a.country,
            region: a.region,
            city: a.city,
            lat: a.lat,
            lng: a.lng,
            hideFromMap: a.hide_from_map || false,
            contact_phone: a.contact_phone,
            contact_email: a.contact_email,
            contact_message: a.contact_message,
            coverageRegions: a.coverage_regions || [],
            date: new Date(a.created_at).toLocaleDateString("fr-FR"),
            video: a.video_url || null,
            videos: a.videos || [],
            seller: getDisplayName(sellerById[a.user_id]),
            verified: sellerById[a.user_id]?.verified || false,
            badge: sellerById[a.user_id]?.badge,
            avatar: sellerById[a.user_id]?.avatar_url,
            views: a.views || 0,
            user_id: a.user_id,
            sellerId: a.user_id,
            horseBreed: a.horse_breed,
            horseAge: a.horse_age,
            horseSex: a.horse_sex,
            horseDiscipline: a.horse_discipline || [],
            horseLevel: a.horse_level,
            horseHeight: a.horse_height,
          };
        });
        setRealAds(formatted);
      }
    } catch(err) {
      console.error("Erreur chargement annonces:", err);
    }
  };

  // New Modern App — nav a 5 emplacements : Accueil / Carte / +Publier (centre) / Messages / Profil.
  // Annonces / Shop / Pros passent en tuiles sur l'accueil-hub (HubTiles).
  const NAV=[
    {id:"home",     label:t.home,     navIcon:"home"},
    {id:"carte",    label:({fr:"Carte",en:"Map",de:"Karte",es:"Mapa",it:"Mappa",pt:"Mapa",nl:"Kaart"}[lang]||"Carte"), navIcon:"map"},
    {id:"__publish__", publish:true, label:({fr:"Publier",en:"Post",de:"Posten",es:"Publicar",it:"Pubblica",pt:"Publicar",nl:"Plaatsen"}[lang]||"Publier")},
    {id:"messages", label:t.messages, navIcon:"msg"},
    {id:"profil",   label:({fr:"Profil",en:"Profile",de:"Profil",es:"Perfil",it:"Profilo",pt:"Perfil",nl:"Profiel"}[lang]||"Profil"), navIcon:"user"},
  ];
  const homeActive = ["home","annonces","shop","pros"].includes(tab);

  return (
    <div style={{
      minHeight: "100vh",
      background: "var(--hc-bg)",
      color: "var(--hc-text)",
      display: "flex",
      flexDirection: "column",
      position: "relative",
      overflow: "hidden",
    }}>
      <G/>
      {!(typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "light") && <NightSparkles count={20}/>}

      {/* Header glassmorphism — compact, logo sticker + brand + actions */}
      <div style={{
        position: "sticky", top: 0, zIndex: 50,
        padding: "calc(env(safe-area-inset-top, 22px) + 10px) 16px 10px",
        background: "var(--hc-bg)",
        backdropFilter: "blur(14px)",
        borderBottom: `1px solid var(--hc-border)`,
        display: "flex", alignItems: "center", gap: 10,
        flexShrink: 0,
      }}>
        {homeActive && tab !== "home" && (
          <motion.button whileTap={{ scale: 0.9 }} onClick={()=>changeTab("home")} aria-label="Retour" style={{
            background: "var(--hc-surface-2)", border: "1px solid var(--hc-border)", borderRadius: 10,
            width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", flexShrink: 0, color: "var(--hc-text)", fontSize: 18,
          }}>←</motion.button>
        )}
        <img
          src="/horseconnex_logo_sticker_rouge.png"
          alt="HorseConnex"
          style={{
            width: 36, height: 36, objectFit: "contain",
            filter: "drop-shadow(0 4px 12px rgba(200,0,26,.5))",
            flexShrink: 0,
          }}
        />
        <div style={{
          fontFamily: "var(--fh)", fontSize: 17, fontWeight: 900,
          color: "var(--hc-text)", letterSpacing: 0.9, flex: 1, minWidth: 0,
        }}>
          HORSE<span style={{
            background: `linear-gradient(135deg, ${C.redLight} 0%, ${C.redAccent} 50%, ${C.red} 100%)`,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
            color: "transparent",
          }}>CONNEX</span>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <motion.button
            whileTap={{ scale: 0.88 }}
            onClick={toggleTheme}
            aria-label={"Thème : " + themeMeta.label}
            title={"Thème : " + themeMeta.label}
            style={{
              width: 34, height: 34,
              borderRadius: "50%",
              background: "var(--hc-surface-2)",
              border: `1px solid var(--hc-border)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer",
              fontSize: 16,
              padding: 0,
            }}
          >
            <span style={{ filter: "drop-shadow(0 0 4px rgba(255,255,255,.35))" }}>{themeMeta.emoji}</span>
          </motion.button>
          <NotificationBell onClick={()=>{ if(isGuest){requireAccount(t.guestNotif);return;} navigate("notifications"); }}/>
        </div>
      </div>

      {/* Contenu scrollable */}
      <div style={{ flex: 1, overflowY: "auto", position: "relative", zIndex: 1, paddingBottom: 110 }}>
        {tab==="home"     && <HubTiles lang={lang} onSelect={(id)=>{ if(id==="carte"){navigate("map",{ads:realAds});} else if(id==="carnet"){ if(isGuest){requireAccount(t.guestProfile);return;} navigate("carnet"); } else if(id==="trails"){ navigate("trails"); } else {changeTab(id);} }}/>}
        {tab==="home"     && <TabHome      onAd={ad=>navigate("adDetail",{ad})} onPublish={()=>{ if(isGuest){requireAccount(t.guestPublish);return;} navigate("publish",{userCats}); }} onAgenda={()=>{ if(isGuest){requireAccount(t.guestAgenda);return;} navigate("agenda"); }} onNotifications={()=>{ if(isGuest){requireAccount(t.guestNotif);return;} navigate("notifications"); }} search={search} setSearch={setSearch} onAdvanced={()=>setShowSearchModal(true)} lang={lang} ads={realAds} filters={filters} user={user} navigate={navigate}/>}
        {tab==="annonces" && <TabAnnonces   onAd={ad=>navigate("adDetail",{ad})} onPublish={()=>{ if(isGuest){requireAccount(t.guestPublish);return;} navigate("publish",{userCats}); }} ads={realAds} lang={lang}/>}
        {tab==="shop"     && <TabMarketplace onItem={item=>navigate("adDetail",{ad:item})} onPublish={()=>{ if(isGuest){requireAccount(t.guestSellGear);return;} navigate("publish",{userCats,presetCat:"gear"}); }} ads={realAds} lang={lang} user={user}/>}
        {tab==="pros"     && <TabPros onPro={p=>navigate("proDetail",{pro:p})} onMap={()=>navigate("map",{mode:"pros",ads:realAds})} lang={lang} user={user}/>}
        {tab==="messages" && <TabMessages   onChat={c=>navigate("chat",{convo:c})} lang={lang}/>}
      </div>

      {/* Bottom Nav — pill flottante moderne (style Linear/Discord)
          avec indicateur animé qui glisse derrière l'onglet actif */}
      <div style={{
        position: "fixed",
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 14px)",
        left: "50%", transform: "translateX(-50%)",
        width: "calc(100% - 24px)", maxWidth: 406,
        zIndex: 100,
        background: "var(--hc-bg)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: `1px solid var(--hc-border)`,
        borderRadius: 28,
        display: "flex", alignItems: "center", justifyContent: "space-around",
        padding: "8px 6px",
        boxShadow: "0 14px 40px rgba(0,0,0,.55), 0 0 0 1px rgba(255,255,255,.04)",
        flexShrink: 0,
      }}>
        {NAV.map(tb=>{
          if (tb.publish) {
            return (
              <motion.button key="publish" whileTap={{ scale: 0.9 }} onClick={()=>{ if(isGuest){requireAccount(t.guestPublish);return;} navigate("publish",{userCats}); }} style={{ background:"none", border:"none", cursor:"pointer", flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4, minWidth:0 }}>
                <div style={{ width:50, height:50, borderRadius:"50%", background:"linear-gradient(135deg, #C8001A 0%, #FF1744 100%)", display:"flex", alignItems:"center", justifyContent:"center", marginTop:-24, boxShadow:"0 8px 20px rgba(200,0,26,.5)", border:"3px solid var(--hc-bg)" }}>
                  <span style={{ color:"#fff", fontSize:28, fontWeight:300, lineHeight:1, marginTop:-2 }}>+</span>
                </div>
                <span style={{ fontSize:8.5, fontFamily:"var(--fh)", fontWeight:800, letterSpacing:0.3, color:"var(--hc-accent)", textTransform:"uppercase", whiteSpace:"nowrap" }}>{tb.label}</span>
              </motion.button>
            );
          }
          const active = tb.id==="home" ? homeActive : tab===tb.id;
          return (
            <motion.button
              key={tb.id}
              whileTap={{ scale: 0.92 }}
              onClick={async()=>{
                // Carte : route plein écran dédiée (pas un tab) -> retour Android OK.
                if (tb.id === "carte") { navigate("map", { ads: realAds }); return; }
                if (tb.id === "profil") { if(isGuest){requireAccount(t.guestProfile);return;} navigate("profil"); return; }
                if (tb.id === "messages" && isGuest) { requireAccount(t.guestMessages); return; }
                changeTab(tb.id);
                if (tb.id === "messages") {
                  try {
                    const authRes = await supabase.auth.getUser();
                    const authUser = authRes.data.user;
                    if (authUser) {
                      await supabase.from("notifications").update({read:true}).eq("user_id",authUser.id).eq("type","message").eq("read",false);
                      setUnreadMessages(0);
                    }
                  } catch(e) {}
                }
              }}
              style={{
                background: "none", border: "none",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                padding: "6px 2px",
                cursor: "pointer", flex: 1,
                position: "relative",
                minWidth: 0,
              }}
            >
              <div style={{
                position: "relative",
                display: "flex", alignItems: "center", justifyContent: "center",
                width: 42, height: 30,
              }}>
                {/* Indicateur de tab actif animé (glissement spring entre onglets) */}
                {active && (
                  <motion.div
                    layoutId="active-tab-pill"
                    transition={{ type: "spring", stiffness: 380, damping: 32 }}
                    style={{
                      position: "absolute", inset: 0,
                      background: "var(--hc-accent-soft)",
                      border: "1px solid var(--hc-accent-soft)",
                      borderRadius: 12,
                    }}
                  />
                )}
                <span style={{ position: "relative", zIndex: 1, display: "flex" }}>
                  <CIcon name={tb.navIcon} s={20} c={active ? "var(--hc-accent)" : "var(--hc-text-muted)"}/>
                </span>
                {tb.id === "messages" && unreadMessages > 0 && (
                  <div style={{
                    position: "absolute", top: -4, right: 0,
                    minWidth: 16, height: 16, borderRadius: 8,
                    background: C.red, color: "#fff",
                    fontSize: 9, fontWeight: 800,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "0 4px",
                    border: "2px solid var(--hc-bg)",
                    zIndex: 2,
                  }}>{unreadMessages > 9 ? "9+" : unreadMessages}</div>
                )}
              </div>
              <span style={{
                fontSize: 8.5,
                fontFamily: "var(--fh)", fontWeight: 700, letterSpacing: 0.3,
                color: active ? "var(--hc-accent)" : "var(--hc-text-faint)",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
                transition: "color .15s",
              }}>{tb.label}</span>
            </motion.button>
          );
        })}
      </div>

      {showSearchModal && <SearchAdvancedModal onClose={()=>setShowSearchModal(false)} onApply={(f)=>setFilters(f)} initialFilters={filters} lang={lang}/>}
      {signupPrompt && <SignupPrompt message={signupPrompt} onClose={()=>setSignupPrompt(null)} onSignup={()=>{setSignupPrompt(null);navigate("register");}} onLogin={()=>{setSignupPrompt(null);navigate("login");}} lang={lang}/>}
    </div>
  );
}

export function ProfilPage({navigate,showToast,userCats=[],user={},setUser,lang="fr"}) {
  const t = useLang(lang);
  const isLight = typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "light";
  return (
    <div style={{
      minHeight:"100vh",
      background: "var(--hc-bg)",
      color: "var(--hc-text)",
      display:"flex", flexDirection:"column",
      position:"relative", overflow:"hidden",
    }}>
      <G/>
      {!isLight && <NightSparkles count={16}/>}

      {/* Header glassmorphism cohérent */}
      <div style={{
        position:"sticky", top:0, zIndex:50,
        padding:"calc(env(safe-area-inset-top, 22px) + 10px) 16px 10px",
        background:"var(--hc-bg)",
        backdropFilter:"blur(14px)",
        borderBottom:`1px solid var(--hc-border)`,
        display:"flex", alignItems:"center", gap:12,
      }}>
        <motion.button
          whileTap={{ scale: 0.92 }}
          onClick={()=>navigate("dash")}
          style={{
            background:"var(--hc-surface-2)",
            border:`1px solid var(--hc-border)`,
            borderRadius:10, width:36, height:36,
            display:"flex", alignItems:"center", justifyContent:"center",
            cursor:"pointer", flexShrink:0,
            color:"var(--hc-text)", fontSize:18,
          }}
        >←</motion.button>
        <div style={{
          fontFamily:"var(--fh)", fontSize:18, fontWeight:900,
          color:"var(--hc-text)", letterSpacing:0.3, flex:1,
        }}>👤 {t.myProfile}</div>
        <img
          src="/horseconnex_logo_sticker_rouge.png"
          alt=""
          style={{
            width:32, height:32, objectFit:"contain",
            filter:"drop-shadow(0 4px 12px rgba(200,0,26,.5))",
          }}
        />
      </div>

      <div style={{flex:1, overflowY:"auto", position:"relative", zIndex:1}}>
        <TabProfil navigate={navigate} showToast={showToast} userCats={userCats} user={user} setUser={setUser} lang={lang}/>
      </div>
    </div>
  );
}

